/**
 * ONE TAP — procedural prop + surface-detail library.
 *
 * Everything the arena is dressed with is generated here from code: crate stacks,
 * pipe runs, vent blocks, server racks, vending banks, fans, antenna clutter,
 * catenary cables, skyline buildings and the tiling textures (grate, hazard, windows).
 *
 * ## The honesty rule
 *
 * ONE TAP is a competitive shooter, so decoration is not allowed to lie about what is
 * solid. Every generator here falls into exactly one of two categories:
 *
 *  - **Skins** (`panelledBlock`, `platedBlock`, `crateStack`, `pipeBundle`, `ventBlock`,
 *    `serverRack`, `vendingBank`, `grateBlock`, `glassBlock`, `signPanel`): these *fill a
 *    collision brush's oriented box*. They keep the brush's full outer shell, so the
 *    silhouette *is* the collision volume; applied detail stands proud of it by a few
 *    centimetres (`SURFACE_RELIEF`), and at most ~20 cm for a fan housing or a sign —
 *    far too shallow for anyone to read as cover. What you see is what you bump into.
 *
 *  - **Thin detail** (`boltRow`, `louvreFace`, `fanRecess`, `conduitRun`, `catenaryCable`,
 *    `antennaCluster`, `hangingSign`, `floorPlate`, `paintedBar`, `groundRing`): centimetre-scale relief on
 *    a real surface, or skeletal clutter placed in air the caller has already verified is
 *    well above every walkable surface. Nothing here is thick enough to read as cover.
 *
 * No generator ever emits a free-standing solid-looking box in playable space. Fake cover
 * loses rounds.
 *
 * Client-only: imports `three` and touches the DOM. Never import this from src/shared.
 */

import * as THREE from 'three';
import { PALETTE, TOON_BANDS } from '@shared/constants';
import type { DecalPlacement, DecalKind } from './decals';

// ---------------------------------------------------------------------------
// Art constants — how far detail may deviate from the collision shell
// ---------------------------------------------------------------------------

/** Max outward relief for any surface detail. Small enough that nobody reads it as cover. */
export const SURFACE_RELIEF = 0.05;
/** Thickness of an applied wall panel. Proud of the shell, so the outline traces it. */
export const PANEL_RELIEF = 0.03;
/**
 * Minimum air gap between a face and any slab applied to it.
 *
 * Zero is not safe: a slab whose back side is exactly coplanar with the shell face makes
 * the two surfaces fight in the shadow depth pass, which shows up as half of every panel
 * going black along its triangulation diagonal. 2 cm is invisible and kills it dead.
 */
export const SURFACE_GAP = 0.02;
/** How far off a face decals sit. Must exceed SURFACE_GAP + PANEL_RELIEF. */
export const DECAL_LIFT = 0.075;
/**
 * The one tint strength every non-team mark in the building is painted at.
 *
 * "One weight, one value" is the whole difference between a signage *system* and a pile
 * of separately-authored decals. Marks used to arrive at 0.4, 0.5, 0.55, 0.8, 0.85, 0.9,
 * 0.95 and 1.0, which is eight different greys on one wall and reads as accident. Every
 * caller now passes this, tinted `PALETTE.ink`; only team paint is allowed to differ,
 * because team paint is carrying different information.
 */
export const SIGNAGE_STRENGTH = 0.9;
/** Gap between adjacent sub-volumes in a skin (crate seams, rack seams). */
export const SEAM_WIDTH = 0.035;
/** Radius of a bolt head. */
export const BOLT_RADIUS = 0.045;
/** Physical size of one grate tile, so the pattern reads at a constant scale everywhere. */
export const GRATE_TILE = 0.55;
/** Physical size of one hazard-stripe repeat. */
export const HAZARD_TILE = 0.9;

// -- Bodies, and what the building must measure against them ----------------

/**
 * Guard rail height, measured from the walking surface.
 *
 * The player is 2.05 m tall (`PLAYER_HEIGHT`), not the 1.8 m a real building is detailed
 * for, so every "human" dimension in the architecture is scaled by the same 1.14 that the
 * body was: a 1.1 m code-minimum rail becomes 1.25 m. At that height the rail crosses a
 * standing player at the hip and a crouched one (1.15 m) at the shoulder, which is exactly
 * the read a rail is supposed to have — and it is the reason a rail is built as posts and
 * two horizontals rather than as a panel. A solid 1.25 m parapet on a brush that collision
 * does not have would be a metre and a quarter of invisible cover on every ledge on the map.
 */
export const RAILING_HEIGHT = 1.25;
/** Metres between rail posts. Real handrail spacing, so the rhythm gives distance away. */
export const RAILING_POST_PITCH = 2.2;
/** Post and rail stock diameter. Thin enough that a rail never reads as a wall. */
export const RAILING_STOCK = 0.06;
/**
 * Planting bed height.
 *
 * Deliberately under `STEP_HEIGHT` (0.45 m): a player walks over a kerb this low without
 * even the sim noticing, so a visual planter that collision does not have changes nothing
 * about a fight. Anything taller would be fake cover, and fake cover loses rounds.
 */
export const PLANTER_HEIGHT = 0.22;
/** Depth of a planting bed run along the foot of a wall. */
export const PLANTER_DEPTH = 0.62;

/**
 * Thickness of the lining plate laid on the underside of a deck.
 *
 * Everything else applied to a soffit — coffer ribs, rooflights, hung ductwork — starts
 * clear of this, because two surfaces sharing a plane fight in the depth pass and the
 * symptom is half a ceiling going black along its triangulation diagonal.
 */
export const SOFFIT_LINING_THICKNESS = 0.04;

/** How far a floor slab stops short of the shell edge, leaving a pale kerb. */
export const FLOOR_INSET = 0.16;
/** Thickness of a floor slab. Proud of the collision face, never behind it. */
export const FLOOR_PLATE_THICKNESS = 0.03;
/** Width of a scored expansion joint between floor bays. */
export const FLOOR_JOINT_WIDTH = 0.05;
/** Joints per axis, capped so a deck the size of a city block stays cheap. */
export const FLOOR_JOINT_MAX = 14;
/** Edge length of one floor bay. Real terrazzo is laid at roughly this scale. */
export const FLOOR_BAY = 4.0;
/**
 * Height of a laid floor's walking surface above the collision top face.
 *
 * Anything painted on a deck — lane lines, spawn rings, hazard bands — has to be placed
 * relative to *this*, not to the brush, or the floor plate swallows it.
 */
export const FLOOR_TOP = SURFACE_GAP + FLOOR_PLATE_THICKNESS;
/** Where a decal painted on a laid floor sits. Clears the slab and its joints. */
export const FLOOR_DECAL_LIFT = FLOOR_TOP + 0.03;

// -- The roof, measured against the 2.05 m body -----------------------------

/**
 * THE PARAPET UPSTAND, and why it is 0.30 m rather than the metre a real one would be.
 *
 * A white concrete upstand running the rim of every roof, with a steel handrail standing
 * on top of it, is *the* Mirror's Edge silhouette — it is what makes a roof edge legible
 * from across the map and what makes falling off one feel deliberate. But collision does
 * not have it: this file only skins brushes, and a solid metre-high decorative parapet
 * would be chest-high cover on every ledge in the arena that a bullet passes straight
 * through. Fake cover loses rounds, and that is not negotiable.
 *
 * So the parapet is split in two, and each half is honest about itself. A 0.30 m solid
 * upstand — comfortably under `STEP_HEIGHT` (0.45 m), so the sim would not even notice
 * it — carries the hard white line along the edge. The `edgeRailing` above it carries the
 * height, and it is posts and two horizontals, which hide nobody. Together they read from
 * fifty metres as one 1.55 m parapet-and-rail, and neither half lies.
 */
export const PARAPET_HEIGHT = 0.3;
/** How far inboard of the deck edge the upstand's inner face sits. */
export const PARAPET_DEPTH = 0.26;
/** Coping stone on top of the upstand: the pale line that draws the edge. */
export const PARAPET_COPING = 0.06;

/**
 * Tallest a rooftop curb, sump or vent kerb may be.
 *
 * Same rule as the planter: anything a player steps over without the simulation noticing
 * is free to exist, anything taller is a lie. `STEP_HEIGHT` is 0.45 m; 0.40 m leaves room
 * for the aluminium hood that sits on top of a vent curb and still clears it.
 */
export const ROOF_CURB_MAX = 0.4;
/** Height of a plain roof curb before its hood. */
export const ROOF_CURB_HEIGHT = 0.22;

/** Galvanised walkway grating: how far the tread sits proud of the roof field. */
export const WALKWAY_LIFT = 0.08;
/** Standard walkway width. Two players wide is a road; this is a service route. */
export const WALKWAY_WIDTH = 1.4;
/** Painted edge stripe either side of a walkway. */
export const WALKWAY_STRIPE = 0.12;

/** One membrane lap-weld repeat, metres. Real single-ply sheet comes in ~2 m rolls. */
export const MEMBRANE_TILE = 2.0;
/** One gravel-ballast texture repeat, metres. */
export const GRAVEL_TILE = 1.6;
/** One safety-banding repeat on rigging hardware, metres. */
export const WARN_TILE = 0.5;
/** One awning stripe repeat, metres. A real canvas stripe is about this wide. */
export const AWNING_TILE = 0.84;

// -- Roof garden: the things that make a roof a place people go on a nice day ------

/**
 * THE HONEST-WIDTH RULE, and why the rooftop planting is columnar rather than leafy.
 *
 * A player is `PLAYER_RADIUS * 2` = 0.88 m across. Anything narrower than that cannot
 * conceal one, whatever its height — which is the same argument that lets a 6 cm handrail
 * post, a 14 cm davit mast and a 16 cm crane leg exist on a map where collision has none of
 * them. So rooftop trees are clipped **columnar evergreens**: 0.5 m across at the widest,
 * 3 m tall, on a tub the sim would step over. A spreading canopy would be prettier and it
 * would also be a soft ceiling over a duel, so it is not on the menu.
 *
 * Everything else in the roof garden is either flat (`reflectPool`, at 16 cm of rim) or
 * see-through (`shadeSail`, translucent canvas; `glassBalustrade`, glazing).
 */
export const TREE_MAX_WIDTH = 0.5;
export const TREE_HEIGHT = 3.0;
/** Tub the tree stands in. Under STEP_HEIGHT, like every other roof kerb. */
export const TREE_TUB_HEIGHT = 0.32;
/** Rim height of a reflecting pool. Flat enough to be invisible to the simulation. */
export const POOL_RIM_HEIGHT = 0.16;
/** How far a shade sail's canvas is held above the deck. Above every standing head. */
export const SAIL_HEIGHT = 3.6;
/** Mast stock for a sail or a windsock. Signpost gauge — hides nobody. */
export const SAIL_MAST_STOCK = 0.08;
/** Height a striped awning's fixing band sits at, above the deck it overlooks. */
export const AWNING_HEIGHT = 2.9;
/** How far an awning projects from the wall it is bolted to. */
export const AWNING_PROJECTION = 0.95;
/** Glass balustrade panel height. Matches RAILING_HEIGHT so edges read as one system. */
export const BALUSTRADE_HEIGHT = RAILING_HEIGHT;

// ---------------------------------------------------------------------------
// Material vocabulary — one key is one merged draw call for the entire arena
// ---------------------------------------------------------------------------

/**
 * The material vocabulary.
 *
 * ## The colour budget (DESIGN.md 4)
 *
 * The arena is white architecture. Everything in the "neutral" group below is a tone of
 * PALETTE.base / base2 / shadow / ink — the same family, separated by **value**, not hue.
 * That separation is the job: a white wall, a clearly darker floor and a bright soffit is
 * what makes a room sit in space. Texture and value carry the interest; hue carries meaning.
 *
 * ## Exactly two saturated hues exist in the world
 *
 * `teamA` (azure) and `teamB` (signal red), and they only ever land on surfaces a team
 * owns — the two ends of the fighting deck. Nothing else in the building is allowed above
 * `WORLD_MAX_SATURATION`. Saturated amber is not in this list at all: it belongs to the
 * gun and to nothing else, so that a chambered weapon is the only warm thing on screen.
 *
 * `zoneA` and `zoneB` are the storey planes. They used to be jade and sun yellow at full
 * chroma, and a five-deck atrium painted in them put green, yellow, red, azure and teal
 * into one frame — six competing hues with no hierarchy, which is the "neon crept back"
 * failure with the glow turned off. They are now **chroma-capped tints separated by
 * value**: one clearly darker than the white walls, one clearly brighter. A storey still
 * names itself at a glance, and it does it in the currency the rest of the look is already
 * spending.
 *
 * Emissive is scarcer still. Two emissive keys exist and they mark one thing each: where
 * your team spawns, and where your team's gun spawns. If anything else glowed, the gun
 * would stop reading — and the gun is the most important object in the game.
 */
export type MatKey =
  // Neutral structure — value separation, no hue (lit, outlined, casts shadow)
  | 'concrete'
  | 'concreteDark'
  | 'panel'
  | 'floor'
  | 'floorDark'
  | 'metal'
  | 'metalDark'
  | 'trim'
  | 'rubber'
  // ROOFTOP SURFACES. The arena is played on the roofs of a white city (DESIGN.md 4,
  // "The Arena Is a ROOFTOP"), so the surface a player spends the whole match looking at
  // is not polished terrazzo — it is a roof. These three are what a real roof is made of,
  // and they are all neutrals: a roof deck reads by texture and by value, never by hue.
  //
  //  - `membrane` is single-ply roofing sheet: a mid pale grey field with welded laps
  //    every 2 m, so a big deck has a direction and a measurable repeat.
  //  - `gravel` is ballast: a lighter, noisier field used on the secondary roofs, so two
  //    adjacent roofs are told apart the way real ones are — by what is on them.
  //  - `rail` is painted steel: handrails, davit frames, walkway stanchions. Graphite, so
  //    every rail in the arena silhouettes hard against a bright sky, which is the single
  //    most useful thing a rooftop handrail can do for a player at speed.
  | 'membrane'
  | 'gravel'
  | 'rail'
  // Mirror's Edge material vocabulary: pale wood and planting. Both are *architecture*
  // rather than signal, and both exist so the building reads as a place people occupy
  // instead of as a greybox.
  //
  // `foliage` is clipped hedge — a mid green mass. `foliageLight` is the sunlit half:
  // tree crowns and the tops of planted beds. Two greens is the difference between "a
  // dark blob in a trough" and "a plant on a bright day", and the playtest asked for the
  // second one ("it could feel a little more sunny... insanely pristine").
  | 'wood'
  | 'foliage'
  | 'foliageLight'
  /**
   * THE TWO LANDMARK PAINTS, and the whole answer to "it still feels a little muted,
   * like there could be a little bit more splashes of colour".
   *
   * A jade mass and a terracotta mass, at real chroma (C* ~40-50, five times the
   * `WORLD_MAX_SATURATION` a storey plane gets), landing on a **hard-capped handful of
   * genuinely monumental blocks** and on nothing else. Few, large and confident: two
   * enormous painted volumes read as design, and thirty small painted trims read as
   * noise. That is the entire difference between this and the rainbow the colour budget
   * was written to kill, and the cap — not the chroma — is what enforces it.
   *
   * Why these two hues and not the obvious ones:
   *
   *  - **Not azure and not signal red.** Those are the two teams. A saturated wall in a
   *    team's hue is a wall that player cannot be seen against.
   *  - **Both land 15-19 L\* above both team colours** (jade L* 75, clay L* 72, against
   *    teamA 56 and teamB 57). So a player silhouette of *either* team keeps a value edge
   *    as well as a hue edge in front of them, which a same-value plane would not give.
   *  - **Warm and cool, one each.** A white-and-blue frame under a blue sky grades cold;
   *    the terracotta mass is what makes it read as a hot bright day rather than as an
   *    overcast render, and the jade answers it.
   */
  | 'accentJade'
  | 'accentClay'
  /**
   * Machinery yellow — and it is **machinery only**, never architecture.
   *
   * Sun yellow at full chroma sits uncomfortably close to `PALETTE.loaded`, the colour of
   * a chambered gun (L* 84 / C* 69 against the gun's 79 / 83), and DESIGN.md 4 makes the
   * gun's amber the single most important readability signal in the project. So this is
   * rationed by *category* rather than by area: cranes, davits, hoists and windsocks —
   * things that are unmistakably equipment — and nothing that is part of a building. The
   * tower cranes that carry most of it stand out in the surrounding city, tens of metres
   * from anything a player can shoot, where a big confident yellow against blue sky costs
   * the gun nothing and buys the skyline its one warm note.
   */
  | 'accentSun'
  /**
   * Striped awning canvas: terracotta and white, at a fixed 0.42 m stripe pitch.
   *
   * Fabric is the one material in the arena that is neither concrete, glass nor steel, and
   * a striped awning over a terrace is about the strongest "beautiful day" signal
   * architecture has. High-frequency stripes also survive distance the way a flat field
   * does not, so an awning stays legible as an awning from across the map.
   */
  | 'awning'
  /**
   * Shade-sail canvas: a bright warm white, **translucent**.
   *
   * Translucent for the same reason `scrimPanel` is: collision has nothing here, so an
   * opaque sheet stretched over a deck would hide a player the simulation says is in the
   * open. You can see straight through this one, which is the only way a big overhead
   * plane is allowed to exist in a shooter.
   */
  | 'canvas'
  /**
   * Still water: a reflecting pool.
   *
   * Pale, low-chroma and *specular* — it is on DESIGN.md 4's material list, it is the only
   * horizontal surface in the arena that catches a bright sky highlight, and it costs the
   * colour budget almost nothing because its interest is a mirrored highlight rather than
   * a hue. Laid flat inside a 16 cm rim, so it is honest by construction.
   */
  | 'water'
  // Storey planes — chroma-capped, told apart by value. Large flat planes only.
  | 'zoneA'
  | 'zoneB'
  // Team WALLS. Pale tinted plaster: chroma capped hard, value lifted into the wall
  // band, so a wall is a wall first and a team's wall second — and so a player standing
  // against one still has a silhouette. See TEAM_WALL_* in arenaMesh.
  | 'teamA'
  | 'teamB'
  // Team ACCENT. Full-chroma team colour, and the ONLY place it appears as paint: the
  // skirting band where a team wall meets the deck, and the spawn pad. A hundred square
  // metres of signal red is a wall you cannot fight in front of; a 30 cm band of it at
  // the foot of that wall says exactly the same thing and costs the frame nothing.
  | 'teamBandA'
  | 'teamBandB'
  // Textured opaque
  | 'hazard'
  | 'grate'
  /**
   * Safety banding on lifting gear: matte diagonal sun-yellow over slate.
   *
   * The one warm accent in the arena that is not the gun, and it is rationed to a few
   * square metres on the grapple davits and crane arms. That is deliberate and it is the
   * opposite of the old glowing-green anchor: a jib arm with yellow-and-black banding on
   * its nose is what real rooftop rigging looks like, the high-frequency stripe survives
   * to 60 m where a flat colour would not, and it is **matte** — it never blooms, so it
   * cannot be confused with the one emissive amber in the game.
   *
   * `makeHazardTexture` stays slate-on-white for the hundreds of metres of ledge striping,
   * for exactly the reason documented there. This is the small, loud version.
   */
  | 'warn'
  // Transparent
  | 'glass'
  /**
   * Skylight glazing. Unlit and near-white-warm, so a roof opening is the brightest
   * thing overhead — which is what stops a soffit reading as a hole punched in the
   * render. Not a "light": it is the sky, seen through a roof.
   */
  | 'skylight'
  /**
   * THERE ARE NO EMISSIVE MATERIALS IN THE ARENA. Not one.
   *
   * There used to be two, `neonTeamA` and `neonTeamB`, marking the spawn pads and the gun
   * pads. Both were rings painted on the ground, and in the last screenshot they were the
   * most saturated thing in the frame by a wide margin while the architecture around them
   * was neutral grey — the exact failure DESIGN.md 4 names when it says that if the most
   * saturated element is painted on the ground, the colour budget has gone to the wrong
   * place. They are gone, and so is the spawn's coloured fill light and the gun pad's
   * light shaft.
   *
   * Outdoors at midday this costs nothing, because none of it worked: an emissive ring on
   * a sunlit white deck is invisible, and a shaft of light over the middle of the map is
   * haze in front of the one thing a player needs to read clearly. What it buys is the
   * rule DESIGN.md 4 calls the most important readability requirement in the project — a
   * chambered gun is now the *only* thing in the world that glows.
   */
  // Special batches. THE CITY: three tiers of aerial perspective rather than two.
  // `skylineNear` is the ring of roofs just beyond the play space — close enough to read
  // parapets, plant rooms and water tanks on, and the thing that makes the arena a piece
  // of a city rather than a diorama on a table. The other two wash toward the sky.
  | 'skylineNear'
  | 'skyline'
  | 'skylineFar'
  | 'skylineGlass'
  | 'void';

/** Materials that receive the inverted-hull ink outline. */
export const OUTLINED_MATS: ReadonlySet<MatKey> = new Set<MatKey>([
  'concrete',
  'concreteDark',
  'panel',
  'floor',
  'floorDark',
  'metal',
  'metalDark',
  'trim',
  'membrane',
  'gravel',
  'rail',
  'wood',
  'foliage',
  'foliageLight',
  'zoneA',
  'zoneB',
  'teamA',
  'teamB',
  'teamBandA',
  'teamBandB',
  'accentJade',
  'accentClay',
  'accentSun',
  // `awning` is deliberately NOT here. It is a pair of flat double-sided quads, and an
  // inverted-hull shell on a zero-thickness surface lands coplanar with the surface it is
  // supposed to be behind. Its `rail` frame — the fixing band, the front bar and the end
  // brackets — carries the line instead, which is where an awning's outline really is.
  'hazard',
  'warn',
  // A skylight without a hard frame line around it is a white blob. The ink shell IS
  // the glazing bar.
  'skylight',
]);

/** Materials that participate in the shadow pass. */
export const SHADOW_MATS: ReadonlySet<MatKey> = new Set<MatKey>([
  'concrete',
  'concreteDark',
  'panel',
  'floor',
  'floorDark',
  'metal',
  'metalDark',
  'trim',
  'rubber',
  'membrane',
  'gravel',
  'rail',
  'wood',
  'foliage',
  'foliageLight',
  'zoneA',
  'zoneB',
  'teamA',
  'teamB',
  'teamBandA',
  'teamBandB',
  'accentJade',
  'accentClay',
  'accentSun',
  'awning',
  'hazard',
  'warn',
]);

/**
 * The sink every generator writes into. Implemented by arenaMesh; passed down so
 * props never need to know about merging, materials or decal atlases.
 */
export interface BuildCtx {
  /** Clone `geo`, transform by `xf`, append to the merge bucket for `mat`. */
  add(mat: MatKey, geo: THREE.BufferGeometry, xf: THREE.Matrix4): void;
  /** Append `geo` directly (takes ownership — the caller must not reuse it). */
  addRaw(mat: MatKey, geo: THREE.BufferGeometry): void;
  /** Paint a decal onto a surface. */
  decal(p: DecalPlacement): void;
  /** Paint by kind, letting the atlas pick a variant. */
  decalKind(kind: DecalKind, p: Omit<DecalPlacement, 'cell'> & { variant?: number }): void;
  /** Deterministic random. Never Math.random — every client must build the same world. */
  rng(): number;
}

// ---------------------------------------------------------------------------
// Cached primitives. Generators reference these; the sink clones on add().
// ---------------------------------------------------------------------------

export const UNIT_BOX = new THREE.BoxGeometry(1, 1, 1);
/** Radius 0.5, height 1, along +Y. */
export const UNIT_CYL = new THREE.CylinderGeometry(0.5, 0.5, 1, 12, 1, false);
export const UNIT_CYL_LOW = new THREE.CylinderGeometry(0.5, 0.5, 1, 6, 1, false);
export const UNIT_CYL_OPEN = new THREE.CylinderGeometry(0.5, 0.5, 1, 14, 1, true);
export const UNIT_SPHERE = new THREE.SphereGeometry(0.5, 10, 7);
export const UNIT_CONE = new THREE.ConeGeometry(0.5, 1, 10, 1, false);
/** Outer radius 0.5, tube 0.1, axis +Z. */
export const UNIT_TORUS = new THREE.TorusGeometry(0.4, 0.1, 8, 20);

const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _vA = new THREE.Vector3();
const _vB = new THREE.Vector3();
const _vC = new THREE.Vector3();
const _vD = new THREE.Vector3();

/** parent * translate * rotate(YXZ) * scale */
export function localXf(
  parent: THREE.Matrix4,
  cx: number,
  cy: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number,
  rx = 0,
  ry = 0,
  rz = 0,
): THREE.Matrix4 {
  _e.set(rx, ry, rz, 'YXZ');
  _q.setFromEuler(_e);
  const m = new THREE.Matrix4();
  m.compose(_vA.set(cx, cy, cz), _q, _vB.set(sx, sy, sz));
  return m.premultiply(parent);
}

export function addBox(
  ctx: BuildCtx,
  mat: MatKey,
  parent: THREE.Matrix4,
  cx: number,
  cy: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number,
  rx = 0,
  ry = 0,
  rz = 0,
): void {
  if (sx <= 0 || sy <= 0 || sz <= 0) return;
  ctx.add(mat, UNIT_BOX, localXf(parent, cx, cy, cz, sx, sy, sz, rx, ry, rz));
}

/** Cylinder along +Y by default. */
export function addCyl(
  ctx: BuildCtx,
  mat: MatKey,
  parent: THREE.Matrix4,
  cx: number,
  cy: number,
  cz: number,
  radius: number,
  len: number,
  rx = 0,
  ry = 0,
  rz = 0,
  lowPoly = false,
): void {
  if (radius <= 0 || len <= 0) return;
  ctx.add(mat, lowPoly ? UNIT_CYL_LOW : UNIT_CYL, localXf(parent, cx, cy, cz, radius * 2, len, radius * 2, rx, ry, rz));
}

export function addSphere(
  ctx: BuildCtx,
  mat: MatKey,
  parent: THREE.Matrix4,
  cx: number,
  cy: number,
  cz: number,
  radius: number,
): void {
  ctx.add(mat, UNIT_SPHERE, localXf(parent, cx, cy, cz, radius * 2, radius * 2, radius * 2));
}

/** A cylinder spanning a→b, both expressed in `parent`'s space. */
export function addTubeBetween(
  ctx: BuildCtx,
  mat: MatKey,
  parent: THREE.Matrix4,
  a: THREE.Vector3,
  b: THREE.Vector3,
  radius: number,
  lowPoly = true,
): void {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dy, dz);
  if (len < 1e-5) return;
  _vA.set(dx / len, dy / len, dz / len);
  _q.setFromUnitVectors(_vC.set(0, 1, 0), _vA);
  const m = new THREE.Matrix4();
  _vB.set((a.x + b.x) * 0.5, (a.y + b.y) * 0.5, (a.z + b.z) * 0.5);
  m.compose(_vB, _q, _vD.set(radius * 2, len, radius * 2));
  m.premultiply(parent);
  ctx.add(mat, lowPoly ? UNIT_CYL_LOW : UNIT_CYL, m);
}

/** Hand a freshly-built geometry to the sink after baking `xf` into it. */
export function addOwned(ctx: BuildCtx, mat: MatKey, geo: THREE.BufferGeometry, xf: THREE.Matrix4): void {
  geo.applyMatrix4(xf);
  ctx.addRaw(mat, geo);
}

// ---------------------------------------------------------------------------
// Box faces — the frame every surface-detail generator works in
// ---------------------------------------------------------------------------

export interface Face {
  /** Outward normal, local space. */
  n: THREE.Vector3;
  /** In-plane axes: `u` runs across the face, `v` runs up it. n === u × v. */
  u: THREE.Vector3;
  v: THREE.Vector3;
  /** Face centre, local space. */
  c: THREE.Vector3;
  /** Extent along u and along v. */
  w: number;
  h: number;
  /** 0:+X 1:-X 2:+Y 3:-Y 4:+Z 5:-Z */
  index: number;
}

const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);

export function boxFaces(half: THREE.Vector3): Face[] {
  const { x, y, z } = half;
  return [
    { index: 0, n: V(1, 0, 0), u: V(0, 0, -1), v: V(0, 1, 0), c: V(x, 0, 0), w: z * 2, h: y * 2 },
    { index: 1, n: V(-1, 0, 0), u: V(0, 0, 1), v: V(0, 1, 0), c: V(-x, 0, 0), w: z * 2, h: y * 2 },
    { index: 2, n: V(0, 1, 0), u: V(1, 0, 0), v: V(0, 0, -1), c: V(0, y, 0), w: x * 2, h: z * 2 },
    { index: 3, n: V(0, -1, 0), u: V(1, 0, 0), v: V(0, 0, 1), c: V(0, -y, 0), w: x * 2, h: z * 2 },
    { index: 4, n: V(0, 0, 1), u: V(1, 0, 0), v: V(0, 1, 0), c: V(0, 0, z), w: x * 2, h: y * 2 },
    { index: 5, n: V(0, 0, -1), u: V(-1, 0, 0), v: V(0, 1, 0), c: V(0, 0, -z), w: x * 2, h: y * 2 },
  ];
}

export const isVerticalFace = (f: Face): boolean => f.index !== 2 && f.index !== 3;

/** Point on a face, local space. `du`/`dv` are metres from the face centre. */
export function facePoint(f: Face, du: number, dv: number, out: THREE.Vector3, lift = 0): THREE.Vector3 {
  out.copy(f.c);
  out.addScaledVector(f.u, du);
  out.addScaledVector(f.v, dv);
  if (lift !== 0) out.addScaledVector(f.n, lift);
  return out;
}

/**
 * Right-handed basis for a face: local X → u, local Y → the outward normal,
 * local Z → u × n. (u, n, v) is left-handed, which would mirror geometry, so the
 * third axis is derived rather than taken from `v`.
 */
function faceBasis(f: Face): THREE.Matrix4 {
  const w = new THREE.Vector3().crossVectors(f.u, f.n);
  return new THREE.Matrix4().makeBasis(f.u, f.n, w);
}

/** parent * faceBasis(f) placed at a point on the face. */
function faceXf(parent: THREE.Matrix4, f: Face, du: number, dv: number, lift: number): THREE.Matrix4 {
  const m = faceBasis(f);
  facePoint(f, du, dv, _vA, lift);
  m.setPosition(_vA);
  return m.premultiply(parent);
}

/** Euler angles that point a +Y primitive along a face normal. */
function faceRot(f: Face): [number, number, number] {
  switch (f.index) {
    case 0:
      return [0, 0, -Math.PI / 2];
    case 1:
      return [0, 0, Math.PI / 2];
    case 2:
      return [0, 0, 0];
    case 3:
      return [Math.PI, 0, 0];
    case 4:
      return [Math.PI / 2, 0, 0];
    default:
      return [-Math.PI / 2, 0, 0];
  }
}

/**
 * A thin slab lying flat on a face. `lift` is the gap between the face and the slab's
 * back side, so the slab occupies [lift, lift + thickness] along the normal.
 *
 * `lift` is clamped to >= SURFACE_GAP on purpose. Additive geometry cannot carve a recess:
 * a slab pushed back into the shell is either invisible or coplanar with the shell face,
 * and coplanar surfaces fight in the depth pass. Panel definition therefore comes from
 * *proud* detail plus the ink outline, never from fake recesses.
 */
export function addFaceSlab(
  ctx: BuildCtx,
  mat: MatKey,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  w: number,
  h: number,
  thickness: number,
  lift: number,
): void {
  if (w <= 0 || h <= 0 || thickness <= 0) return;
  const m = faceXf(parent, f, du, dv, Math.max(SURFACE_GAP, lift) + thickness * 0.5);
  m.multiply(new THREE.Matrix4().makeScale(w, thickness, h));
  ctx.add(mat, UNIT_BOX, m);
}

/** A ring lying flat on a face, axis along the face normal. */
function addFaceTorus(
  ctx: BuildCtx,
  mat: MatKey,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  lift: number,
  outerRadius: number,
): void {
  const m = faceXf(parent, f, du, dv, lift);
  m.multiply(new THREE.Matrix4().makeRotationX(Math.PI / 2));
  m.multiply(new THREE.Matrix4().makeScale(outerRadius * 2, outerRadius * 2, outerRadius * 2));
  ctx.add(mat, UNIT_TORUS, m);
}

/**
 * Roll for a decal placed on a face of a brush rotated by `yaw`.
 * Vertical faces already resolve "up" correctly from the normal; horizontal faces
 * need the brush's yaw so painted marks line up with the block they sit on.
 */
export function faceDecalRoll(f: Face, yaw: number): number {
  if (f.index === 2) return -yaw;
  if (f.index === 3) return yaw;
  return 0;
}

// ---------------------------------------------------------------------------
// Skins — these fill a collision brush exactly
// ---------------------------------------------------------------------------

export interface SkinOpts {
  /** Base material for the shell. */
  base: MatKey;
  /** Secondary material for recesses and inner panels. */
  inner: MatKey;
  /**
   * Trim material for applied strips and machined detail. Neutral by default — a region's
   * hue reaches a brush through `base`, as a whole painted plane, never as piping.
   */
  accent: MatKey;
  /** Which faces are exposed (not buried in neighbouring geometry). Indexed like `Face.index`. */
  exposed: boolean[];
  /** Brush yaw, needed to orient decals on horizontal faces. */
  yaw: number;
}

const shell = (ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, mat: MatKey): void => {
  addBox(ctx, mat, parent, 0, 0, 0, half.x * 2, half.y * 2, half.z * 2);
};

/** Largest exposed vertical face, or null when the block is fully buried. */
function pickFront(faces: Face[], o: SkinOpts): Face | null {
  let best: Face | null = null;
  for (const f of faces) {
    if (!isVerticalFace(f) || !o.exposed[f.index]) continue;
    if (!best || f.w * f.h > best.w * best.h) best = f;
  }
  return best;
}

/** Push a decal onto a face in one call, handling the local→world transform. */
function faceDecal(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  o: SkinOpts,
  kind: DecalKind,
  du: number,
  dv: number,
  width: number,
  color: number,
  extra?: { strength?: number; glow?: boolean; variant?: number; height?: number; lift?: number },
): void {
  ctx.decalKind(kind, {
    // Default lift clears the tallest applied panel relief, so a mark painted across a
    // panel joint never disappears into the plate it is sitting on.
    pos: facePoint(f, du, dv, new THREE.Vector3(), extra?.lift ?? DECAL_LIFT).applyMatrix4(parent),
    normal: f.n.clone().transformDirection(parent),
    width,
    height: extra?.height,
    color,
    strength: extra?.strength,
    glow: extra?.glow,
    variant: extra?.variant,
    rotation: faceDecalRoll(f, o.yaw),
  });
}

/**
 * Default architecture treatment: recessed panels, chamfered vertical corners,
 * bolt columns. Reads as poured concrete / bolted panel construction.
 */
export function panelledBlock(ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
  shell(ctx, parent, half, o.base);
  const faces = boxFaces(half);

  for (const f of faces) {
    if (!o.exposed[f.index] || f.index === 3) continue;
    if (f.w < 1.2 || f.h < 1.2) continue;

    // Panels stay roughly a constant physical size, so a 90 m wall reads at the same
    // scale as a 4 m one instead of turning into four enormous slabs.
    const cols = Math.max(1, Math.min(12, Math.round(f.w / 3.2)));
    const rows = Math.max(1, Math.min(10, Math.round(f.h / 3.2)));
    const margin = 0.22;
    const pw = (f.w - margin * 2) / cols;
    const ph = (f.h - margin * 2) / rows;
    if (pw < 0.5 || ph < 0.5) continue;

    // Big architectural faces get self-coloured recesses (shape does the work);
    // small blocks get a contrasting inner so they stay busy up close.
    const inner: MatKey = f.w * f.h > 40 ? o.base : o.inner;

    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < rows; j++) {
        const du = -f.w * 0.5 + margin + pw * (i + 0.5);
        const dv = -f.h * 0.5 + margin + ph * (j + 0.5);
        const inset = 0.12;
        // Applied panel, not a carved recess: a 3 cm plate with its own ink outline.
        addFaceSlab(ctx, inner, parent, f, du, dv, pw - inset * 2, ph - inset * 2, PANEL_RELIEF, 0);
      }
    }

    if (ctx.rng() < 0.7) {
      const n = Math.max(2, Math.min(8, Math.round(f.h / 1.4)));
      boltRow(ctx, parent, f, -f.w * 0.5 + margin * 0.55, n);
      boltRow(ctx, parent, f, f.w * 0.5 - margin * 0.55, n);
    }
  }

  // Chamfer strips down the four vertical corners: pure silhouette definition.
  if (half.y > 0.5) {
    const t = 0.09;
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        addBox(ctx, 'trim', parent, sx * (half.x - t * 0.5), 0, sz * (half.z - t * 0.5), t, half.y * 2 - 0.04, t);
      }
    }
  }
}

/** Brushed-metal construction: big plates, rivets, a diagonal brace, an inspection hatch. */
export function platedBlock(ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
  shell(ctx, parent, half, o.base);
  const faces = boxFaces(half);
  for (const f of faces) {
    if (!o.exposed[f.index] || f.index === 3) continue;
    if (f.w < 0.9 || f.h < 0.9) continue;

    const plates = Math.max(1, Math.min(6, Math.round(f.w / 1.8)));
    for (let i = 1; i < plates; i++) {
      const du = -f.w * 0.5 + (f.w / plates) * i;
      addFaceSlab(ctx, 'trim', parent, f, du, 0, 0.05, f.h - 0.1, 0.02, 0);
    }
    const n = Math.max(2, Math.min(8, Math.round(f.h / 1.1)));
    boltRow(ctx, parent, f, -f.w * 0.5 + 0.16, n);
    boltRow(ctx, parent, f, f.w * 0.5 - 0.16, n);

    if (f.w > 2.6 && f.h > 2.2 && ctx.rng() < 0.55) {
      const ang = Math.atan2(f.h, f.w);
      const len = Math.hypot(f.w, f.h) - 0.5;
      const m = faceXf(parent, f, 0, 0, SURFACE_RELIEF * 0.5);
      m.multiply(new THREE.Matrix4().makeRotationY(ang));
      m.multiply(new THREE.Matrix4().makeScale(len, 0.045, 0.22));
      ctx.add(o.accent, UNIT_BOX, m);
    }

    if (f.h > 1.8 && f.w > 1.8 && ctx.rng() < 0.45) {
      const du = (ctx.rng() - 0.5) * (f.w - 1.4);
      const dv = -f.h * 0.5 + 0.75;
      // An inspection hatch: a dark recessed plate with a machined lip. The asset barcode
      // that used to be stencilled across it was a scribble — unreadable by design, so it
      // answered no question, and at the two metres a player passes it at it read as a
      // smudge on the paint rather than as a label.
      addFaceSlab(ctx, 'metalDark', parent, f, du, dv, 0.8, 0.9, 0.035, 0);
      addFaceSlab(ctx, 'trim', parent, f, du, dv, 0.86, 0.06, 0.04, 0);
    }
  }
}

/**
 * A stack of shipping crates that exactly fills the brush. The outermost faces stay
 * on the collision boundary; only the seams between crates are cut inward.
 */
export function crateStack(ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
  const nx = Math.max(1, Math.min(3, Math.round(half.x / 0.85)));
  const ny = Math.max(1, Math.min(3, Math.round(half.y / 0.85)));
  const nz = Math.max(1, Math.min(3, Math.round(half.z / 0.85)));
  const cx = (half.x * 2) / nx;
  const cy = (half.y * 2) / ny;
  const cz = (half.z * 2) / nz;

  const span = (i: number, n: number, cell: number, min: number): { c: number; s: number } => {
    const lo = i === 0 ? 0 : SEAM_WIDTH;
    const hi = i === n - 1 ? 0 : SEAM_WIDTH;
    const cellMin = min + cell * i;
    return { c: cellMin + lo + (cell - lo - hi) * 0.5, s: cell - lo - hi };
  };

  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      for (let k = 0; k < nz; k++) {
        const ax = span(i, nx, cx, -half.x);
        const ay = span(j, ny, cy, -half.y);
        const az = span(k, nz, cz, -half.z);
        const r = ctx.rng();
        const mat: MatKey = r < 0.2 ? o.accent : r < 0.42 ? 'metalDark' : o.base;
        addBox(ctx, mat, parent, ax.c, ay.c, az.c, ax.s, ay.s, az.s);

        // Corner brackets on the crate's vertical edges.
        const bt = 0.07;
        for (const sx of [-1, 1]) {
          for (const sz of [-1, 1]) {
            addBox(
              ctx,
              'metalDark',
              parent,
              ax.c + sx * (ax.s * 0.5 - bt * 0.5),
              ay.c,
              az.c + sz * (az.s * 0.5 - bt * 0.5),
              bt,
              ay.s * 0.98,
              bt,
            );
          }
        }
        // Banding strap.
        addBox(ctx, 'trim', parent, ax.c, ay.c + ay.s * 0.18, az.c, ax.s + 0.01, 0.07, az.s + 0.01);
      }
    }
  }

  // One stencilled bay number on the stack's front face only, centred, at the kit's single
  // ink weight. Not one per face: a crate stencilled on all four sides is a crate nobody
  // set out, and the warning triangle that used to sit beside the number is gone too — a
  // hazard glyph on a packing crate is not information anybody acts on, and every extra
  // glyph shape spent here is a shape the player has to learn to ignore.
  const front = pickFront(boxFaces(half), o);
  if (front && front.w >= 0.8 && front.h >= 0.8) {
    faceDecal(ctx, parent, front, o, 'numeral', 0, 0, Math.min(0.45, front.w * 0.2), PALETTE.ink, {
      strength: SIGNAGE_STRENGTH,
      variant: (ctx.rng() * 10) | 0,
    });
  }
}

/**
 * Bundled service pipes running the long axis of the brush, with flanges and brackets.
 *
 * `backed` controls the one thing that matters here for fairness. On a *collision* brush
 * the bundle gets an opaque backing shell, so gaps between pipes never show through to the
 * skybox — the brush is solid, and the render says so. On a **decorative** brush collision
 * skips the volume entirely, so the backing shell has to go: a 120 m opaque bar you can
 * walk and shoot through but cannot see through is precisely the lie this library exists
 * to refuse. Unbacked, the pipes themselves are all you see, and you can see between them.
 */
export function pipeBundle(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  half: THREE.Vector3,
  o: SkinOpts,
  backed = true,
): void {
  if (backed) shell(ctx, parent, half, 'metalDark');

  const alongX = half.x >= half.z;
  const len = (alongX ? half.x : half.z) * 2;
  const spanU = (alongX ? half.z : half.x) * 2;
  const spanV = half.y * 2;
  const rows = Math.max(1, Math.min(4, Math.floor(spanV / 0.6)));
  const cols = Math.max(1, Math.min(4, Math.floor(spanU / 0.6)));
  const rStep = spanV / rows;
  const cStep = spanU / cols;
  const radius = Math.min(rStep, cStep) * 0.44;
  const rx = alongX ? 0 : Math.PI / 2;
  const rz = alongX ? Math.PI / 2 : 0;

  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const dv = -half.y + rStep * (j + 0.5);
      const du = -(spanU * 0.5) + cStep * (i + 0.5);
      const r = ctx.rng();
      const mat: MatKey = r < 0.22 ? o.accent : r < 0.55 ? 'metal' : 'metalDark';
      const px = alongX ? 0 : du;
      const pz = alongX ? du : 0;
      addCyl(ctx, mat, parent, px, dv, pz, radius, len, rx, 0, rz);

      const flanges = Math.max(1, Math.floor(len / 3.2));
      for (let k = 1; k <= flanges; k++) {
        const t = -len * 0.5 + (len / (flanges + 1)) * k;
        addCyl(ctx, 'metalDark', parent, alongX ? t : px, dv, alongX ? pz : t, radius * 1.22, 0.14, rx, 0, rz);
      }
    }
  }

  // Support collars clamped around the bundle.
  const brackets = Math.max(2, Math.floor(len / 3.6));
  for (let k = 0; k < brackets; k++) {
    const t = -len * 0.5 + (len / (brackets - 1)) * k;
    addBox(
      ctx,
      'trim',
      parent,
      alongX ? t : 0,
      0,
      alongX ? 0 : t,
      alongX ? 0.1 : half.x * 2,
      half.y * 2 - 0.04,
      alongX ? half.z * 2 : 0.1,
    );
  }

  // A valve wheel on the largest exposed end cap.
  const front = pickFront(boxFaces(half), o);
  if (front && Math.min(front.w, front.h) > 0.9) {
    addFaceTorus(ctx, 'metal', parent, front, 0, 0, -0.02, Math.min(front.w, front.h) * 0.28);
    addCyl(ctx, 'metalDark', parent, front.c.x, front.c.y, front.c.z, 0.07, 0.16, ...faceRot(front));
  }
}

/** HVAC block: louvred intakes, a fan behind a guard, corner bolts, frame rails. */
export function ventBlock(ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
  shell(ctx, parent, half, 'metalDark');
  let didFan = false;
  for (const f of boxFaces(half)) {
    if (!o.exposed[f.index] || f.index === 3) continue;
    if (f.w < 0.7 || f.h < 0.7) continue;
    if (!didFan && isVerticalFace(f) && f.w > 1.4 && f.h > 1.4) {
      fanRecess(ctx, parent, f, 0, 0, Math.min(f.w, f.h) * 0.34);
      didFan = true;
    } else {
      louvreFace(ctx, parent, f, 0, 0, f.w - 0.32, f.h - 0.32);
    }
    boltRow(ctx, parent, f, -f.w * 0.5 + 0.12, 2);
    boltRow(ctx, parent, f, f.w * 0.5 - 0.12, 2);
  }
  // Frame rails so the block still reads as a solid box in silhouette.
  const t = 0.13;
  for (const sy of [-1, 1]) {
    addBox(ctx, 'metal', parent, 0, sy * (half.y - t * 0.5), 0, half.x * 2, t, half.z * 2);
  }
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      addBox(ctx, 'metal', parent, sx * (half.x - t * 0.5), 0, sz * (half.z - t * 0.5), t, half.y * 2, t);
    }
  }
}

/**
 * Rack of relay units with machined status strips and drive slots.
 *
 * Deliberately unlit: a cabinet that glows is a cabinet competing with the gun for
 * the player's eye, and it has nothing to tell them. Value contrast between the dark
 * chassis and the pale strip does the same visual job for free.
 */
export function serverRack(ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
  shell(ctx, parent, half, 'trim');
  const front = pickFront(boxFaces(half), o);
  if (!front) return;

  const units = Math.max(2, Math.min(12, Math.floor((half.y * 2) / 0.42)));
  const uh = (half.y * 2 - 0.16) / units;
  for (let i = 0; i < units; i++) {
    const dv = -half.y + 0.08 + uh * (i + 0.5);
    addFaceSlab(ctx, 'metalDark', parent, front, 0, dv, front.w - 0.16, uh - 0.05, 0.05, 0);
    addFaceSlab(
      ctx,
      ctx.rng() < 0.75 ? 'panel' : 'metal',
      parent,
      front,
      -front.w * 0.5 + 0.26,
      dv,
      Math.min(0.5, front.w * 0.3),
      Math.min(0.05, uh * 0.22),
      0.02,
      0.05,
    );
    const slots = Math.max(1, Math.floor((front.w - 0.7) / 0.28));
    for (let s = 0; s < slots; s++) {
      const du = -front.w * 0.5 + 0.55 + s * 0.28;
      if (du > front.w * 0.5 - 0.2) break;
      addFaceSlab(ctx, 'trim', parent, front, du, dv, 0.2, uh * 0.5, 0.02, 0.05);
    }
  }
  for (const sx of [-1, 1]) {
    addBox(ctx, 'metal', parent, sx * (half.x - 0.06), 0, 0, 0.12, half.y * 2, half.z * 2);
  }
  addBox(ctx, 'metal', parent, 0, half.y - 0.06, 0, half.x * 2, 0.12, half.z * 2);
}

/** A bank of dispenser cabinets: white product walls behind glass, in dark surrounds. */
export function vendingBank(ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
  shell(ctx, parent, half, 'trim');
  const front = pickFront(boxFaces(half), o);
  if (!front || front.h < 1.2) {
    panelledBlock(ctx, parent, half, o);
    return;
  }
  const n = Math.max(1, Math.min(4, Math.round(front.w / 1.3)));
  const cw = front.w / n;
  for (let i = 0; i < n; i++) {
    const du = -front.w * 0.5 + cw * (i + 0.5);
    const gw = Math.max(0.2, cw - 0.34);
    const gh = front.h * 0.54;
    const gv = -front.h * 0.06;

    // Layered strictly outward: product wall, shelves, glass, then the surround.
    addFaceSlab(ctx, 'panel', parent, front, du, gv, gw - 0.04, gh - 0.04, 0.02, 0.01);
    const rows = Math.max(2, Math.floor(gh / 0.42));
    for (let r = 0; r < rows; r++) {
      addFaceSlab(ctx, 'trim', parent, front, du, gv - gh * 0.5 + (gh / rows) * (r + 0.5), gw, 0.04, 0.03, 0.03);
    }
    addFaceSlab(ctx, 'glass', parent, front, du, gv, gw, gh, 0.02, 0.07);
    // Cabinet surround as four border strips, so the lit window stays visible.
    const side = (cw - gw) * 0.5;
    for (const sx of [-1, 1]) {
      addFaceSlab(ctx, 'metalDark', parent, front, du + sx * (gw + side) * 0.5, 0, side - 0.04, front.h - 0.06, 0.1, 0);
    }
    addFaceSlab(ctx, 'metalDark', parent, front, du, gv + gh * 0.5 + 0.12, cw - 0.08, 0.24, 0.1, 0);
    addFaceSlab(ctx, 'metalDark', parent, front, du, gv - gh * 0.5 - 0.2, cw - 0.08, 0.4, 0.1, 0);

    // Header board over each cabinet, left blank. It used to carry a "notice plate" decal
    // — a page-of-ruled-text glyph — which read as a document icon pasted onto the wall at
    // every distance anyone actually saw it from. A blank white board under an ink lip is
    // architecture; the icon was clip art.
    addFaceSlab(ctx, 'panel', parent, front, du, front.h * 0.5 - 0.28, cw - 0.2, 0.34, 0.05, 0.06);
    addFaceSlab(ctx, 'trim', parent, front, du, -front.h * 0.5 + 0.24, cw - 0.36, 0.26, 0.06, 0.1);
  }
}

/**
 * The walking surface of a platform: a polished slab, inset from the shell edge, scored
 * into bays by expansion joints.
 *
 * This exists to solve the single worst readability failure a white arena can have — floor,
 * wall and ceiling landing on the same tone, so the frame reads as fog (DESIGN.md 4). The
 * slab is a *darker* neutral than the walls that rise off it, the joints give the eye a
 * receding grid to measure distance against, and the pale kerb where slab meets shell draws
 * a hard line along every edge you can walk off. Value and texture, no hue.
 *
 * 3 cm proud of the collision top face, so what you stand on is what you see.
 */
export function floorPlate(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  mat: MatKey,
  bay: number,
): void {
  const w = f.w - FLOOR_INSET * 2;
  const h = f.h - FLOOR_INSET * 2;
  if (w <= 0.2 || h <= 0.2) return;
  addFaceSlab(ctx, mat, parent, f, 0, 0, w, h, FLOOR_PLATE_THICKNESS, 0);

  // Expansion joints. Capped so a 100 m deck does not mint a thousand boxes.
  const cols = Math.min(FLOOR_JOINT_MAX, Math.floor(w / bay));
  const rows = Math.min(FLOOR_JOINT_MAX, Math.floor(h / bay));
  const jointLift = SURFACE_GAP + FLOOR_PLATE_THICKNESS;
  for (let i = 1; i < cols; i++) {
    addFaceSlab(ctx, 'trim', parent, f, -w * 0.5 + (w / cols) * i, 0, FLOOR_JOINT_WIDTH, h, 0.012, jointLift);
  }
  for (let j = 1; j < rows; j++) {
    addFaceSlab(ctx, 'trim', parent, f, 0, -h * 0.5 + (h / rows) * j, w, FLOOR_JOINT_WIDTH, 0.012, jointLift);
  }
}

/** See-through grating: an alpha-tested sheet in a metal frame. Honest about sightlines. */
export function grateBlock(ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, _o: SkinOpts): void {
  const thinAxis = half.x <= half.y && half.x <= half.z ? 0 : half.y <= half.z ? 1 : 2;
  const w = thinAxis === 0 ? half.z * 2 : half.x * 2;
  const h = thinAxis === 1 ? half.z * 2 : half.y * 2;
  const t = Math.min(0.11, (thinAxis === 0 ? half.x : thinAxis === 1 ? half.y : half.z) * 1.6);

  // Border rails at full brush extent so the silhouette is honest.
  if (thinAxis === 1) {
    addBox(ctx, 'metalDark', parent, 0, 0, half.z - t * 0.5, half.x * 2, half.y * 2, t);
    addBox(ctx, 'metalDark', parent, 0, 0, -half.z + t * 0.5, half.x * 2, half.y * 2, t);
    addBox(ctx, 'metalDark', parent, half.x - t * 0.5, 0, 0, t, half.y * 2, half.z * 2);
    addBox(ctx, 'metalDark', parent, -half.x + t * 0.5, 0, 0, t, half.y * 2, half.z * 2);
  } else {
    addBox(ctx, 'metalDark', parent, 0, half.y - t * 0.5, 0, half.x * 2, t, half.z * 2);
    addBox(ctx, 'metalDark', parent, 0, -half.y + t * 0.5, 0, half.x * 2, t, half.z * 2);
    if (thinAxis === 0) {
      addBox(ctx, 'metalDark', parent, 0, 0, half.z - t * 0.5, half.x * 2, half.y * 2, t);
      addBox(ctx, 'metalDark', parent, 0, 0, -half.z + t * 0.5, half.x * 2, half.y * 2, t);
    } else {
      addBox(ctx, 'metalDark', parent, half.x - t * 0.5, 0, 0, t, half.y * 2, half.z * 2);
      addBox(ctx, 'metalDark', parent, -half.x + t * 0.5, 0, 0, t, half.y * 2, half.z * 2);
    }
  }

  // The sheet itself, with world-scaled UVs so the pattern never stretches.
  const rot: [number, number, number] =
    thinAxis === 0 ? [0, Math.PI / 2, 0] : thinAxis === 1 ? [-Math.PI / 2, 0, 0] : [0, 0, 0];
  addOwned(
    ctx,
    'grate',
    texturedQuad(w, h, w / GRATE_TILE, h / GRATE_TILE),
    localXf(parent, 0, 0, 0, 1, 1, 1, rot[0], rot[1], rot[2]),
  );

  // Cross ribs for depth.
  const ribs = Math.max(1, Math.min(8, Math.floor(w / 1.5)));
  for (let i = 1; i < ribs; i++) {
    const d = -w * 0.5 + (w / ribs) * i;
    if (thinAxis === 0) addBox(ctx, 'metalDark', parent, 0, 0, d, half.x * 2, half.y * 2, 0.06);
    else addBox(ctx, 'metalDark', parent, d, 0, 0, 0.06, half.y * 2, half.z * 2);
  }
}

/** Glass curtain walling in a slate mullion frame. */
export function glassBlock(ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
  shell(ctx, parent, half, 'glass');
  const t = Math.min(0.1, Math.min(half.x, Math.min(half.y, half.z)) * 0.9);
  for (const sy of [-1, 1]) {
    addBox(ctx, 'trim', parent, 0, sy * (half.y - t * 0.5), 0, half.x * 2, t, half.z * 2);
  }
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      addBox(ctx, 'trim', parent, sx * (half.x - t * 0.5), 0, sz * (half.z - t * 0.5), t, half.y * 2, t);
    }
  }
  const thinAxis = half.x <= half.z ? 0 : 2;
  const spanLen = thinAxis === 0 ? half.z * 2 : half.x * 2;
  const bays = Math.max(1, Math.min(8, Math.round(spanLen / 2.2)));
  for (let i = 1; i < bays; i++) {
    const d = -spanLen * 0.5 + (spanLen / bays) * i;
    if (thinAxis === 0) addBox(ctx, 'trim', parent, 0, 0, d, half.x * 2 + 0.01, half.y * 2, 0.08);
    else addBox(ctx, 'trim', parent, d, 0, 0, 0.08, half.y * 2, half.z * 2 + 0.01);
  }
  // Capping rail. Brushed aluminium, not a light: a curtain wall is architecture,
  // and architecture in this game does not glow.
  addBox(ctx, 'metal', parent, 0, half.y - t * 1.4, 0, half.x * 2 + 0.02, 0.06, half.z * 2 + 0.02);
}

/**
 * A painted signage plate: one flat plane of a single colour in a slate frame.
 *
 * This is the arena's wayfinding vocabulary made physical — the "red stairwell, jade
 * atrium, yellow catwalk" of DESIGN.md 4. It replaces the emissive slab this used to be:
 * a lip strip or a hung banner carries information by *being a large flat colour*, and it
 * needs no glow to do that under a bright sky.
 *
 * Only ever called on plates thin enough that nobody could read them as cover, **and** —
 * when the brush is decorative — only where the caller has proved the plate is bolted to
 * something solid. A free-hanging opaque board is handled by `scrimPanel` instead.
 */
export function signPanel(ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
  const thin = Math.min(half.x, half.y, half.z);
  const t = Math.min(0.07, thin * 0.55);
  shell(ctx, parent, half, o.accent);
  // Frame the plate on its two largest-perimeter edges so it reads as a mounted panel.
  for (const sy of [-1, 1]) {
    addBox(ctx, 'trim', parent, 0, sy * (half.y - t * 0.5), 0, half.x * 2 + 0.012, t, half.z * 2 + 0.012);
  }
  if (thin === half.y) return; // a horizontal strip needs no side frame
  for (const sx of [-1, 1]) {
    addBox(ctx, 'trim', parent, sx * (half.x - t * 0.5), 0, 0, t, half.y * 2 + 0.012, half.z * 2 + 0.012);
  }
}

/**
 * A hung banner: a slate perimeter frame with a **translucent** scrim stretched in it.
 *
 * This is what a decorative signage plate becomes when it is *not* bolted to a wall —
 * the banners swinging in the middle of the atrium, the boards hung over open air. They
 * are invisible to collision, so an opaque version would block a sightline that nothing
 * else in the world blocks: you could stand behind one, see nothing, and be shot through
 * it. That is the single worst thing decoration can do in a shooter, and it is worth more
 * than it costs to give it up.
 *
 * A scrim keeps every bit of the wayfinding value. It is still a large flat field of the
 * region's colour, still framed, still carries a stencilled name — it just does not hide
 * a player. The frame is drawn at the full brush extent so the silhouette stays honest
 * about where the banner is.
 */
export function scrimPanel(ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
  const thin = Math.min(half.x, half.y, half.z);
  const t = Math.min(0.09, thin * 0.8);
  // The sheet: inset a frame's width so the frame reads as holding it.
  const inset = Math.min(t * 2, Math.min(half.x, Math.min(half.y, half.z)) * 0.5);
  addBox(
    ctx,
    'glass',
    parent,
    0,
    0,
    0,
    Math.max(0.02, half.x * 2 - (thin === half.x ? 0 : inset)),
    Math.max(0.02, half.y * 2 - (thin === half.y ? 0 : inset)),
    Math.max(0.02, half.z * 2 - (thin === half.z ? 0 : inset)),
  );

  // Perimeter frame at the brush's real extent, on the two axes that are not the thin one.
  const rail = (axis: 'x' | 'y' | 'z'): void => {
    if (thin === half[axis]) return;
    for (const s of [-1, 1]) {
      addBox(
        ctx,
        'trim',
        parent,
        axis === 'x' ? s * (half.x - t * 0.5) : 0,
        axis === 'y' ? s * (half.y - t * 0.5) : 0,
        axis === 'z' ? s * (half.z - t * 0.5) : 0,
        axis === 'x' ? t : half.x * 2 + 0.012,
        axis === 'y' ? t : half.y * 2 + 0.012,
        axis === 'z' ? t : half.z * 2 + 0.012,
      );
    }
  };
  rail('y');
  rail('x');
  rail('z');

  // A painted band of the region's colour across the head of the banner, so the hue still
  // lands as a flat plane even though the body of the sheet is see-through.
  if (o.accent !== 'panel' && thin !== half.y && half.y > 0.6) {
    const bandH = Math.min(half.y * 0.5, 0.9);
    addBox(
      ctx,
      o.accent,
      parent,
      0,
      half.y - t - bandH * 0.5,
      0,
      half.x * 2 + 0.014,
      bandH,
      half.z * 2 + 0.014,
    );
  }
}

// ---------------------------------------------------------------------------
// Thin surface detail
// ---------------------------------------------------------------------------

/** A column of bolt heads at `du` across the face. */
export function boltRow(ctx: BuildCtx, parent: THREE.Matrix4, f: Face, du: number, count: number): void {
  if (count < 1) return;
  const rot = faceRot(f);
  const span = Math.max(0, f.h - 0.3);
  for (let i = 0; i < count; i++) {
    const dv = count === 1 ? 0 : -span * 0.5 + (span / (count - 1)) * i;
    facePoint(f, du, dv, _vA, SURFACE_RELIEF * 0.4);
    ctx.add(
      'metalDark',
      UNIT_CYL_LOW,
      localXf(parent, _vA.x, _vA.y, _vA.z, BOLT_RADIUS * 2, SURFACE_RELIEF * 0.8, BOLT_RADIUS * 2, rot[0], rot[1], rot[2]),
    );
  }
}

/** Angled louvre slats recessed into a face. */
export function louvreFace(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  w: number,
  h: number,
): void {
  if (w <= 0.2 || h <= 0.2) return;
  // Dark backing plate, then slats standing proud of it.
  addFaceSlab(ctx, 'trim', parent, f, du, dv, w, h, 0.03, 0);
  const n = Math.max(2, Math.min(40, Math.floor(h / 0.16)));
  const step = h / n;
  for (let i = 0; i < n; i++) {
    const y = -h * 0.5 + step * (i + 0.5);
    const m = faceXf(parent, f, du, dv, 0.045);
    m.multiply(new THREE.Matrix4().makeTranslation(0, 0, -y));
    m.multiply(new THREE.Matrix4().makeRotationX(0.6));
    m.multiply(new THREE.Matrix4().makeScale(w, 0.03, step * 0.85));
    ctx.add('metal', UNIT_BOX, m);
  }
}

/** A fan recessed behind a guard grille. Entirely inside the brush — zero protrusion. */
export function fanRecess(ctx: BuildCtx, parent: THREE.Matrix4, f: Face, du: number, dv: number, radius: number): void {
  const rot = faceRot(f);
  // Surface-mounted housing collar. Everything else nests inside it, proud of the shell,
  // because geometry pushed behind the shell face would simply be invisible.
  facePoint(f, du, dv, _vA, 0.09);
  ctx.add(
    'trim',
    UNIT_CYL_OPEN,
    localXf(parent, _vA.x, _vA.y, _vA.z, radius * 2.15, 0.18, radius * 2.15, rot[0], rot[1], rot[2]),
  );
  facePoint(f, du, dv, _vA, 0.05);
  ctx.add(
    'metalDark',
    UNIT_CYL,
    localXf(parent, _vA.x, _vA.y, _vA.z, radius * 0.45, 0.1, radius * 0.45, rot[0], rot[1], rot[2]),
  );
  for (let i = 0; i < 6; i++) {
    const m = faceXf(parent, f, du, dv, 0.05);
    m.multiply(new THREE.Matrix4().makeRotationY((i / 6) * Math.PI * 2));
    m.multiply(new THREE.Matrix4().makeTranslation(radius * 0.5, 0, 0));
    m.multiply(new THREE.Matrix4().makeRotationX(0.5));
    m.multiply(new THREE.Matrix4().makeScale(radius * 0.9, 0.03, radius * 0.42));
    ctx.add('metal', UNIT_BOX, m);
  }
  for (let i = 0; i < 5; i++) {
    const m = faceXf(parent, f, du, dv, 0.15);
    m.multiply(new THREE.Matrix4().makeTranslation(0, 0, -radius + (radius * 2 * (i + 0.5)) / 5));
    m.multiply(new THREE.Matrix4().makeScale(radius * 1.9, 0.03, 0.05));
    ctx.add('metalDark', UNIT_BOX, m);
  }
  addFaceTorus(ctx, 'metalDark', parent, f, du, dv, 0.17, radius * 1.25);
}

/**
 * Conduit pipes hugging a wall face. Protrusion is capped well under anything a
 * player would read as standable or as cover.
 */
export function conduitRun(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  dv: number,
  count: number,
  radius = 0.055,
  accent: MatKey = 'metal',
): void {
  const len = Math.max(0, f.w - 0.1);
  if (len <= 0) return;
  for (let i = 0; i < count; i++) {
    const off = (i - (count - 1) * 0.5) * radius * 2.6;
    const m = faceXf(parent, f, 0, dv + off, radius + 0.01);
    m.multiply(new THREE.Matrix4().makeRotationZ(Math.PI * 0.5));
    m.multiply(new THREE.Matrix4().makeScale(radius * 2, len, radius * 2));
    ctx.add(i === count - 1 ? accent : 'metalDark', UNIT_CYL_LOW, m);
  }
  const clamps = Math.max(2, Math.min(12, Math.floor(len / 2.4)));
  for (let i = 0; i < clamps; i++) {
    const du = -len * 0.5 + (len / (clamps - 1)) * i;
    addFaceSlab(ctx, 'metal', parent, f, du, dv, 0.09, radius * 2.6 * count + 0.1, radius * 2 + 0.03, 0);
  }
}

/**
 * A cable strung between two world points with real catenary sag, plus end clamps.
 * Callers must guarantee the whole span sits high above every walkable surface.
 */
export function catenaryCable(
  ctx: BuildCtx,
  a: THREE.Vector3,
  b: THREE.Vector3,
  sag: number,
  radius: number,
  mat: MatKey = 'rubber',
): void {
  const segs = 12;
  const id = new THREE.Matrix4();
  const prev = new THREE.Vector3();
  const cur = new THREE.Vector3();
  const s = sag * (0.7 + ctx.rng() * 0.6);
  const k = 2.2;
  const norm = 1 - Math.cosh(k * 0.5);
  for (let i = 0; i <= segs; i++) {
    const t = i / segs;
    cur.lerpVectors(a, b, t);
    cur.y -= s * ((Math.cosh(k * (t - 0.5)) - Math.cosh(k * 0.5)) / norm);
    if (i > 0) addTubeBetween(ctx, mat, id, prev, cur, radius);
    prev.copy(cur);
  }
  for (const p of [a, b]) {
    ctx.add('metalDark', UNIT_BOX, localXf(id, p.x, p.y, p.z, radius * 5, radius * 5, radius * 5));
  }
}

/** The lowest point of the catenary `catenaryCable` will draw, for clearance checks. */
export function catenaryLowestY(a: THREE.Vector3, b: THREE.Vector3, sag: number): number {
  return Math.min(a.y, b.y) - sag * 1.3;
}

/**
 * Wall-mounted signage: a white board in a slate frame on a bracket. Protrudes at most
 * 18 cm from the wall it is bolted to; the caller supplies the artwork decal at
 * the returned anchor point.
 *
 * A real building's signage is printed, not lit. Keeping it matte means the only things
 * in frame emitting light are the ones a player has to react to.
 */
export function hangingSign(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  w: number,
  h: number,
  face: MatKey,
): { pos: THREE.Vector3; normal: THREE.Vector3 } {
  addFaceSlab(ctx, 'trim', parent, f, du, dv, w, h, 0.1, 0);
  addFaceSlab(ctx, face, parent, f, du, dv, w - 0.14, h - 0.14, 0.06, 0.08);
  addFaceSlab(ctx, 'metalDark', parent, f, du, dv + h * 0.5 + 0.06, w * 0.45, 0.08, 0.12, 0);
  return {
    pos: facePoint(f, du, dv, new THREE.Vector3(), 0.155).applyMatrix4(parent),
    normal: f.n.clone().transformDirection(parent),
  };
}

// ---------------------------------------------------------------------------
// Rooftop / atrium language — the things that make a box read as a building
// ---------------------------------------------------------------------------

/**
 * A guard rail along the top edge of a vertical face: posts, a top rail, a mid rail and
 * a pale wood cap.
 *
 * This is the single most Mirror's Edge object in the kit and it is doing three jobs at
 * once. It tells the eye where a deck ends before the player is close enough to see the
 * drop. It gives a long white plane a rhythm to measure distance against. And the wood
 * cap is the only warm material in the building, which is what a rooftop handrail is
 * actually made of and what stops an all-white arena reading as a render rather than a
 * place.
 *
 * **See-through by construction.** `RAILING_HEIGHT` is over a metre; collision has nothing
 * there; so if this were ever drawn as a solid parapet it would be invisible cover along
 * every ledge in the arena. Posts and two horizontals cannot hide anybody.
 */
export function edgeRailing(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  topDv: number,
  length: number,
  inset: number,
): void {
  if (length < 1.2) return;
  const r = RAILING_STOCK * 0.5;
  const posts = Math.max(2, Math.min(16, Math.round(length / RAILING_POST_PITCH) + 1));
  // Posts, standing on the deck just inboard of the edge. Six-sided: a rail post is 6 cm
  // across and there are thousands of them, so the silhouette is all that survives anyway.
  for (let i = 0; i < posts; i++) {
    const du = -length * 0.5 + (length / (posts - 1)) * i;
    const p = facePoint(f, du, topDv, _vA, -inset);
    addCyl(ctx, 'rail', parent, p.x, p.y + RAILING_HEIGHT * 0.5, p.z, r * 1.35, RAILING_HEIGHT, 0, 0, 0, true);
  }
  // Top rail and one mid rail. Two horizontals, never three: a ladder of rails reads as a
  // fence, and a fence reads as something you cannot cross.
  //
  // Painted steel, not the pale wood cap this used to carry. Wood is a terrace handrail;
  // a service roof edge is galvanised or painted steel, and graphite is what makes a rail
  // hold its line against an open sky — the background this rail now spends its whole life
  // in front of. Wood survives on the planter kerbs and the water tanks, where it is true.
  const a = facePoint(f, -length * 0.5, topDv, new THREE.Vector3(), -inset);
  const b = facePoint(f, length * 0.5, topDv, new THREE.Vector3(), -inset);
  for (const [h, mat, rad] of [
    [RAILING_HEIGHT, 'rail', r * 1.6],
    [RAILING_HEIGHT * 0.52, 'rail', r],
  ] as [number, MatKey, number][]) {
    addTubeBetween(
      ctx,
      mat,
      parent,
      new THREE.Vector3(a.x, a.y + h, a.z),
      new THREE.Vector3(b.x, b.y + h, b.z),
      rad,
      false,
    );
  }
}

/**
 * THE ROOF FIELD — the surface the whole match is played on.
 *
 * `floorPlate` lays polished terrazzo, which is what an atrium floor is. A roof is not a
 * floor: it is a waterproofing system, and it looks like one. Single-ply membrane comes in
 * two-metre rolls lapped and welded along their edges, so a roof deck has a *direction* and
 * a two-metre repeat running across it — which is worth more to a player than a polished
 * plane is, because it is a ruler laid on the ground for judging distance and speed.
 *
 * The sheet is laid as a world-UV quad rather than as a scaled box, so the lap pitch is a
 * real physical size everywhere: a 90 m deck and a 6 m one show the same size lap, and the
 * texture never stretches. 5 cm proud of the collision face, like every other laid surface
 * here — what you stand on is what you see.
 */
export function roofField(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  mat: MatKey,
  tile: number,
): void {
  const w = f.w - FLOOR_INSET * 2;
  const h = f.h - FLOOR_INSET * 2;
  if (w <= 0.2 || h <= 0.2) return;

  facePoint(f, 0, 0, _vA, FLOOR_TOP);
  const m = new THREE.Matrix4().makeTranslation(_vA.x, _vA.y, _vA.z).premultiply(parent);
  addOwned(ctx, mat, texturedFloorQuad(w, h, w / tile, h / tile), m);

  // Welded laps, standing a couple of millimetres proud. Capped like the floor's expansion
  // joints so a deck the size of a city block stays cheap.
  const along = w >= h;
  const span = along ? h : w;
  const laps = Math.min(FLOOR_JOINT_MAX * 2, Math.floor(span / tile));
  for (let i = 1; i < laps; i++) {
    const t = -span * 0.5 + (span / laps) * i;
    addFaceSlab(
      ctx,
      'trim',
      parent,
      f,
      along ? 0 : t,
      along ? t : 0,
      along ? w : 0.055,
      along ? 0.055 : h,
      0.008,
      FLOOR_TOP,
    );
  }
}

/**
 * The white upstand along a roof edge, and the coping that caps it.
 *
 * See `PARAPET_HEIGHT` for the whole argument: this is the bottom 30 cm of a parapet, the
 * part that is allowed to be solid because it is under `STEP_HEIGHT`. `edgeRailing` supplies
 * the height, skeletally. Read together at any distance they are one parapet; read by a
 * bullet they are a 30 cm kerb and some 6 cm posts, which is exactly what they are.
 *
 * The coping is brushed aluminium: a hard bright line drawn along every edge in the arena,
 * which under a low sun is the single most legible thing a roof edge can have.
 */
export function roofParapet(ctx: BuildCtx, parent: THREE.Matrix4, f: Face, topDv: number, length: number): void {
  if (length < 0.8) return;
  const wall = faceXf(parent, f, 0, topDv + PARAPET_HEIGHT * 0.5, 0);
  wall.multiply(new THREE.Matrix4().makeScale(length, PARAPET_DEPTH, PARAPET_HEIGHT));
  ctx.add('panel', UNIT_BOX, wall);

  const cap = faceXf(parent, f, 0, topDv + PARAPET_HEIGHT + PARAPET_COPING * 0.5, 0);
  cap.multiply(new THREE.Matrix4().makeScale(length + 0.1, PARAPET_DEPTH + 0.09, PARAPET_COPING));
  ctx.add('metal', UNIT_BOX, cap);

  // Shadow reveal under the coping. A parapet without one is a extruded rectangle; with
  // one it is a built thing, and the dark line survives to any distance.
  const reveal = faceXf(parent, f, 0, topDv + PARAPET_HEIGHT - 0.05, 0);
  reveal.multiply(new THREE.Matrix4().makeScale(length + 0.02, PARAPET_DEPTH + 0.03, 0.035));
  ctx.add('trim', UNIT_BOX, reveal);
}

/**
 * A vent curb with an aluminium hood on it: the commonest object on any real roof.
 *
 * Total height is capped at `ROOF_CURB_MAX`, under `STEP_HEIGHT`, so the sim would step
 * over it without noticing and nobody can crouch behind it. `du`/`dv` are metres from the
 * centre of the deck's top face.
 */
export function ventCurb(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  w: number,
  d: number,
): void {
  if (w < 0.3 || d < 0.3) return;
  const curb = faceXf(parent, f, du, dv, FLOOR_TOP + ROOF_CURB_HEIGHT * 0.5);
  curb.multiply(new THREE.Matrix4().makeScale(w, ROOF_CURB_HEIGHT, d));
  ctx.add('concrete', UNIT_BOX, curb);

  const hoodH = ROOF_CURB_MAX - ROOF_CURB_HEIGHT;
  const hood = faceXf(parent, f, du, dv, FLOOR_TOP + ROOF_CURB_HEIGHT + hoodH * 0.5);
  hood.multiply(new THREE.Matrix4().makeScale(w + 0.12, hoodH, d + 0.12));
  ctx.add('metal', UNIT_BOX, hood);

  const lip = faceXf(parent, f, du, dv, FLOOR_TOP + ROOF_CURB_HEIGHT + hoodH - 0.02);
  lip.multiply(new THREE.Matrix4().makeScale(w + 0.18, 0.03, d + 0.18));
  ctx.add('trim', UNIT_BOX, lip);
}

/** A roof drain: a shallow sump ring with a domed leaf strainer in it. Ankle height. */
export function roofDrain(ctx: BuildCtx, parent: THREE.Matrix4, f: Face, du: number, dv: number, radius: number): void {
  if (radius < 0.15) return;
  const rot = faceRot(f);
  facePoint(f, du, dv, _vA, FLOOR_TOP + 0.015);
  ctx.add(
    'trim',
    UNIT_CYL_LOW,
    localXf(parent, _vA.x, _vA.y, _vA.z, radius * 2, 0.03, radius * 2, rot[0], rot[1], rot[2]),
  );
  facePoint(f, du, dv, _vA, FLOOR_TOP + 0.05);
  ctx.add(
    'metalDark',
    UNIT_SPHERE,
    localXf(parent, _vA.x, _vA.y, _vA.z, radius * 1.1, radius * 0.9, radius * 1.1, rot[0], rot[1], rot[2]),
  );
}

/**
 * A galvanised grating walkway laid across a roof, with painted edge striping.
 *
 * This is the piece of rooftop language that does the most gameplay work: it is a *route*
 * drawn on the ground in a material nothing else uses, so a player crossing an eighty-metre
 * white deck at speed has a line to follow without reading anything. It is see-through
 * grating, it sits 8 cm proud, and it hides nothing.
 */
export function walkwayRun(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  length: number,
  along: boolean,
): void {
  if (length < 2) return;
  const w = along ? length : WALKWAY_WIDTH;
  const d = along ? WALKWAY_WIDTH : length;

  // The tread. World-scaled UVs so the mesh size is constant everywhere. Placed through
  // `facePoint` rather than by adding to `f.c` directly, because the top face's v axis runs
  // along -Z and hand-rolled offsets get that backwards.
  facePoint(f, du, dv, _vA, FLOOR_TOP + WALKWAY_LIFT);
  const m = new THREE.Matrix4().makeTranslation(_vA.x, _vA.y, _vA.z).premultiply(parent);
  addOwned(ctx, 'grate', texturedFloorQuad(w, d, w / GRATE_TILE, d / GRATE_TILE), m);

  // Kerb angles down both long sides, and a painted stripe outboard of each.
  const half = WALKWAY_WIDTH * 0.5;
  for (const s of [-1, 1]) {
    const kerb = faceXf(
      parent,
      f,
      du + (along ? 0 : s * half),
      dv + (along ? s * half : 0),
      FLOOR_TOP + WALKWAY_LIFT * 0.5,
    );
    kerb.multiply(new THREE.Matrix4().makeScale(along ? length : 0.06, WALKWAY_LIFT, along ? 0.06 : length));
    ctx.add('rail', UNIT_BOX, kerb);

    const stripe = faceXf(
      parent,
      f,
      du + (along ? 0 : s * (half + WALKWAY_STRIPE * 0.5 + 0.04)),
      dv + (along ? s * (half + WALKWAY_STRIPE * 0.5 + 0.04) : 0),
      FLOOR_TOP,
    );
    stripe.multiply(
      new THREE.Matrix4().makeScale(along ? length : WALKWAY_STRIPE, 0.012, along ? WALKWAY_STRIPE : length),
    );
    ctx.add('panel', UNIT_BOX, stripe);
  }
}

/**
 * A rooflight let into a roof deck, seen from **above**: a bright glazed pane on a low
 * upstand curb, in an aluminium frame with glazing bars.
 *
 * `skylightPanel` is the same object seen from underneath. This one matters more, because
 * a player runs across it: it is the brightest thing on a white deck, it says "there is a
 * building under this roof", and — being a flat pane on a 14 cm curb — it is honest, since
 * that curb is well under `STEP_HEIGHT`.
 */
export function deckSkylight(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  w: number,
  h: number,
): void {
  if (w < 0.8 || h < 0.8) return;
  const curbH = 0.14;
  const frame = Math.min(0.16, Math.min(w, h) * 0.11);

  const curb = faceXf(parent, f, du, dv, FLOOR_TOP + curbH * 0.5);
  curb.multiply(new THREE.Matrix4().makeScale(w + 0.16, curbH, h + 0.16));
  ctx.add('panel', UNIT_BOX, curb);

  addFaceSlab(ctx, 'skylight', parent, f, du, dv, w - frame * 2, h - frame * 2, 0.05, FLOOR_TOP + curbH);
  for (const [ou, ov, sw, sh] of [
    [0, (h - frame) * 0.5, w, frame],
    [0, -(h - frame) * 0.5, w, frame],
    [-(w - frame) * 0.5, 0, frame, h - frame * 2],
    [(w - frame) * 0.5, 0, frame, h - frame * 2],
  ] as [number, number, number, number][]) {
    addFaceSlab(ctx, 'metal', parent, f, du + ou, dv + ov, sw, sh, 0.09, FLOOR_TOP + curbH);
  }

  const along = w >= h;
  const span = along ? w : h;
  const bars = Math.max(1, Math.min(6, Math.round(span / 1.5)));
  for (let i = 1; i < bars; i++) {
    const t = -span * 0.5 + (span / bars) * i;
    addFaceSlab(
      ctx,
      'metal',
      parent,
      f,
      du + (along ? t : 0),
      dv + (along ? 0 : t),
      along ? 0.07 : w - frame * 2,
      along ? h - frame * 2 : 0.07,
      0.03,
      FLOOR_TOP + curbH + 0.055,
    );
  }
}

/**
 * A free-standing planter sitting on a roof deck: a pale trough with clipped greenery in it.
 *
 * `plantingBed` only works against a vertical face, and a roof's interest is on the
 * horizontal. This is the same idea laid flat, and it is worth having twice because planting
 * is the only *soft* silhouette in a world made entirely of straight lines — it is what
 * keeps a white roof from reading as a greybox, and DESIGN.md 4 lists it as a material.
 *
 * The trough is `PLANTER_HEIGHT` (0.22 m) and the greenery tops out around 0.4 m: both well
 * under `STEP_HEIGHT`, so a player runs straight over it and nobody can crouch behind it.
 * Real hedges on a real terrace are waist-high; these cannot be, and that is the trade —
 * honest scale loses to honest cover every time.
 */
export function roofPlanter(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  length: number,
  along: boolean,
): void {
  if (length < 1.2) return;
  const d = PLANTER_DEPTH * 1.4;
  const w = along ? length : d;
  const dd = along ? d : length;

  const trough = faceXf(parent, f, du, dv, FLOOR_TOP + PLANTER_HEIGHT * 0.5);
  trough.multiply(new THREE.Matrix4().makeScale(w, PLANTER_HEIGHT, dd));
  ctx.add('panel', UNIT_BOX, trough);
  const lip = faceXf(parent, f, du, dv, FLOOR_TOP + PLANTER_HEIGHT);
  lip.multiply(new THREE.Matrix4().makeScale(w + 0.07, 0.05, dd + 0.07));
  ctx.add('trim', UNIT_BOX, lip);

  const clumps = Math.max(2, Math.min(12, Math.round(length / 0.85)));
  for (let i = 0; i < clumps; i++) {
    const t = -length * 0.5 + (length / clumps) * (i + 0.5);
    const ch = 0.13 + ctx.rng() * 0.11;
    const cl = (length / clumps) * (0.74 + ctx.rng() * 0.2);
    const g = faceXf(
      parent,
      f,
      du + (along ? t : 0),
      dv + (along ? 0 : t),
      FLOOR_TOP + PLANTER_HEIGHT + ch * 0.5 - 0.03,
    );
    g.multiply(new THREE.Matrix4().makeScale(along ? cl : d * 0.8, ch, along ? d * 0.8 : cl));
    ctx.add('foliage', UNIT_BOX, g);

    // The sunlit half. A hedge lit from above is two greens, not one, and the toon ramp
    // cannot supply the second one because a clipped box's top face and its sides band
    // identically. Painting it in costs one slab per clump and is the difference between
    // planting that looks alive on a bright day and planting that looks like a dark brick.
    const cap = faceXf(
      parent,
      f,
      du + (along ? t : 0),
      dv + (along ? 0 : t),
      FLOOR_TOP + PLANTER_HEIGHT + ch - 0.05,
    );
    cap.multiply(new THREE.Matrix4().makeScale(along ? cl * 0.88 : d * 0.7, 0.05, along ? d * 0.7 : cl * 0.88));
    ctx.add('foliageLight', UNIT_BOX, cap);
  }
}

/**
 * A CLIPPED COLUMNAR TREE in a tub — the roof garden's vertical note.
 *
 * The playtest asked for a place that feels like "a beautiful day, insanely pristine", and
 * a trimmed evergreen in a stone tub is about the most direct way architecture says that.
 * It is also the only *tall* soft shape in an arena otherwise made of straight white lines,
 * so it does real work breaking a long deck up at eye level.
 *
 * **Columnar, not spreading, and that is a fairness decision.** See `TREE_MAX_WIDTH`: at
 * 0.5 m across it is narrower than a player (0.88 m), so it cannot conceal one at any
 * height — the same rule that lets a handrail post and a davit mast exist on brushes
 * collision does not have. A canopy would be a soft ceiling over a duel and is not allowed.
 */
export function roofTree(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  height = TREE_HEIGHT,
): void {
  const tubW = TREE_MAX_WIDTH + 0.34;
  const tub = faceXf(parent, f, du, dv, FLOOR_TOP + TREE_TUB_HEIGHT * 0.5);
  tub.multiply(new THREE.Matrix4().makeScale(tubW, TREE_TUB_HEIGHT, tubW));
  ctx.add('panel', UNIT_BOX, tub);
  const rim = faceXf(parent, f, du, dv, FLOOR_TOP + TREE_TUB_HEIGHT);
  rim.multiply(new THREE.Matrix4().makeScale(tubW + 0.08, 0.05, tubW + 0.08));
  ctx.add('trim', UNIT_BOX, rim);
  // Bark mulch, so the tub is not a white box with a stick in it.
  const soil = faceXf(parent, f, du, dv, FLOOR_TOP + TREE_TUB_HEIGHT + 0.01);
  soil.multiply(new THREE.Matrix4().makeScale(tubW - 0.16, 0.03, tubW - 0.16));
  ctx.add('rubber', UNIT_BOX, soil);

  const trunkH = height * 0.26;
  const p = facePoint(f, du, dv, _vA, FLOOR_TOP + TREE_TUB_HEIGHT + trunkH * 0.5);
  addCyl(ctx, 'wood', parent, p.x, p.y, p.z, 0.075, trunkH, 0, 0, 0, true);

  // Four stacked, slightly rotated tiers that taper to a point: a clipped cypress read,
  // which holds a recognisable silhouette against sky at any distance.
  const tiers = 4;
  const crownH = height - trunkH - TREE_TUB_HEIGHT;
  for (let i = 0; i < tiers; i++) {
    const t = i / tiers;
    const w = TREE_MAX_WIDTH * (1 - t * 0.62);
    const th = (crownH / tiers) * 1.14;
    const yy = FLOOR_TOP + TREE_TUB_HEIGHT + trunkH + (crownH / tiers) * (i + 0.5);
    const m = faceXf(parent, f, du, dv, yy);
    m.multiply(new THREE.Matrix4().makeRotationY(0.22 + i * 0.31));
    m.multiply(new THREE.Matrix4().makeScale(w, th, w));
    ctx.add(i === tiers - 1 ? 'foliageLight' : 'foliage', UNIT_BOX, m);
  }
  // The sunlit crest.
  const tip = faceXf(parent, f, du, dv, FLOOR_TOP + TREE_TUB_HEIGHT + trunkH + crownH + 0.06);
  tip.multiply(new THREE.Matrix4().makeRotationY(0.5));
  tip.multiply(new THREE.Matrix4().makeScale(TREE_MAX_WIDTH * 0.34, 0.28, TREE_MAX_WIDTH * 0.34));
  ctx.add('foliageLight', UNIT_BOX, tip);
}

/**
 * A REFLECTING POOL let into a roof deck: a pale stone rim with still water inside it.
 *
 * Still water is on DESIGN.md 4's material list and it is the cheapest "beautiful day" the
 * arena can buy — it is the one horizontal surface that catches a specular sky highlight,
 * so a deck with a pool on it has a bright moving note in it that no amount of white
 * concrete can produce. It is also the most honest object in this whole file: the rim is
 * `POOL_RIM_HEIGHT` (16 cm), the water is flat, and the sim would not notice either.
 */
export function reflectPool(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  w: number,
  d: number,
): void {
  if (w < 1.4 || d < 1.4) return;
  const rimW = 0.34;
  // Four rim slabs rather than one box, so the water reads as being *in* something.
  for (const [ou, ov, sw, sd] of [
    [0, (d - rimW) * 0.5, w, rimW],
    [0, -(d - rimW) * 0.5, w, rimW],
    [-(w - rimW) * 0.5, 0, rimW, d - rimW * 2],
    [(w - rimW) * 0.5, 0, rimW, d - rimW * 2],
  ] as [number, number, number, number][]) {
    const m = faceXf(parent, f, du + ou, dv + ov, FLOOR_TOP + POOL_RIM_HEIGHT * 0.5);
    m.multiply(new THREE.Matrix4().makeScale(sw, POOL_RIM_HEIGHT, sd));
    ctx.add('panel', UNIT_BOX, m);
  }
  // A dark basin under the water, so the pool has depth rather than reading as a painted
  // rectangle, and the water sheet just under the rim line.
  const basin = faceXf(parent, f, du, dv, FLOOR_TOP + 0.02);
  basin.multiply(new THREE.Matrix4().makeScale(w - rimW * 1.6, 0.02, d - rimW * 1.6));
  ctx.add('trim', UNIT_BOX, basin);
  const sheet = faceXf(parent, f, du, dv, FLOOR_TOP + POOL_RIM_HEIGHT * 0.68);
  sheet.multiply(new THREE.Matrix4().makeScale(w - rimW * 1.8, 0.02, d - rimW * 1.8));
  ctx.add('water', UNIT_BOX, sheet);
}

/**
 * A SHADE SAIL on four masts.
 *
 * The biggest single "it is a beautiful day" statement available, and the reason it is
 * allowed to exist over a fighting deck is that the canvas is **translucent** — you can see
 * a player through it, so it occludes nothing collision does not. That is the same bargain
 * `scrimPanel` makes with the hung banners, and it is not negotiable: an opaque sheet
 * stretched 3.6 m over open deck would be a soft ceiling nobody can shoot through.
 *
 * The masts are `SAIL_MAST_STOCK` (8 cm) — signpost gauge, hiding nobody — and they rake
 * outward slightly so the sail reads as tensioned rather than as a flat lid.
 */
export function shadeSail(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  w: number,
  d: number,
): void {
  if (w < 2.5 || d < 2.5) return;
  const hw = w * 0.5;
  const hd = d * 0.5;
  for (const su of [-1, 1]) {
    for (const sv of [-1, 1]) {
      // Own vectors, not the module scratch: `addTubeBetween` uses _vA.._vD internally, so a
      // point still held in one of them is garbage the moment the first tube is emitted.
      const base = facePoint(f, du + su * hw, dv + sv * hd, new THREE.Vector3(), FLOOR_TOP);
      const top = facePoint(
        f,
        du + su * hw * 1.06,
        dv + sv * hd * 1.06,
        new THREE.Vector3(),
        FLOOR_TOP + SAIL_HEIGHT,
      );
      addTubeBetween(ctx, 'rail', parent, base, top, SAIL_MAST_STOCK * 0.5, true);
      // A stay back to the deck, so the mast is obviously in tension.
      const anchor = facePoint(
        f,
        du + su * hw * 0.42,
        dv + sv * hd * 0.42,
        new THREE.Vector3(),
        FLOOR_TOP + 0.06,
      );
      addTubeBetween(ctx, 'rubber', parent, top, anchor, 0.02, true);
    }
  }
  // The canvas: two overlapping sheets at slightly different heights, which is what gives a
  // real sail its hipped, tensioned look without any curved geometry.
  for (const [k, lift] of [
    [1.0, SAIL_HEIGHT],
    [0.72, SAIL_HEIGHT + 0.34],
  ] as [number, number][]) {
    const m = faceXf(parent, f, du, dv, FLOOR_TOP + lift);
    m.multiply(new THREE.Matrix4().makeScale(w * k, 0.04, d * k));
    ctx.add('canvas', UNIT_BOX, m);
  }
}

/**
 * A WINDSOCK on a mast: aviation orange-yellow, and the only thing on the roof that says
 * which way the wind is blowing.
 *
 * A helipad without one is a painted circle. It is also `accentSun` — machinery yellow —
 * which is exactly the category that colour is rationed to, and at roughly a third of a
 * square metre it is a bright note rather than a field.
 */
export function windsock(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  height: number,
  facing: number,
): void {
  const base = faceXf(parent, f, du, dv, FLOOR_TOP + 0.05);
  base.multiply(new THREE.Matrix4().makeScale(0.5, 0.1, 0.5));
  ctx.add('trim', UNIT_BOX, base);
  const p = facePoint(f, du, dv, _vA, FLOOR_TOP + height * 0.5);
  addCyl(ctx, 'rail', parent, p.x, p.y, p.z, SAIL_MAST_STOCK * 0.5, height, 0, 0, 0, true);

  // The hoop the sock is laced to, then three tapering sleeves streaming downwind. Held in
  // its own vector because `addCyl` above has already overwritten the module scratch.
  const top = facePoint(f, du, dv, new THREE.Vector3(), FLOOR_TOP + height);
  // UNIT_TORUS's axis is +Z, so a yaw of `facing` alone lays the hoop's mouth downwind.
  const hoop = new THREE.Matrix4()
    .makeTranslation(top.x, top.y, top.z)
    .multiply(new THREE.Matrix4().makeRotationY(facing))
    .multiply(new THREE.Matrix4().makeScale(0.44, 0.44, 0.44));
  ctx.add('metal', UNIT_TORUS, hoop.premultiply(parent));
  for (let i = 0; i < 3; i++) {
    const t = i / 3;
    const r = 0.34 * (1 - t * 0.55);
    const m = new THREE.Matrix4()
      .makeTranslation(top.x, top.y - t * 0.16, top.z)
      .multiply(new THREE.Matrix4().makeRotationY(facing))
      .multiply(new THREE.Matrix4().makeTranslation(0.34 + t * 0.62, 0, 0))
      .multiply(new THREE.Matrix4().makeScale(0.56, r, r));
    ctx.add(i % 2 === 0 ? 'accentSun' : 'panel', UNIT_BOX, m.premultiply(parent));
  }
}

/**
 * A STRIPED AWNING bolted along a wall face, overlooking the deck at its foot.
 *
 * Terracotta-and-white canvas is the single most efficient "sunny day" cue in the whole
 * kit: it is warm, it is fabric, it throws a hard diagonal shadow, and the high-frequency
 * stripe stays legible at a range where a flat coloured band has washed out.
 *
 * Sized to be honest. It hangs at `AWNING_HEIGHT` (2.9 m, above every standing head — the
 * player is 2.05 m), it projects `AWNING_PROJECTION` (0.95 m), and it is a 5 cm sheet
 * rather than a box, so it is a thin plane hugging a wall that already blocks everything
 * behind it. Nobody can stand behind an awning, and nothing that could be shot at is
 * hidden by one.
 */
export function awningRun(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  length: number,
): void {
  if (length < 1.6) return;
  const proj = AWNING_PROJECTION;
  const tilt = 0.34;

  /**
   * The canvas. `faceXf` hands us a frame in which local X runs along the face, local +Y is
   * the outward normal and local +Z runs DOWN it, so a rotation about local X tips the
   * sheet outward and down — the rake a real awning has. `texturedQuad` is authored in XY
   * facing +Z, which in that frame is exactly the awning's own plane, spanning the length
   * across and the projection outward. UVs repeat along the length only, so the stripes run
   * out from the wall at a constant physical pitch on an awning of any size.
   */
  const sheet = faceXf(parent, f, du, dv, 0);
  sheet.multiply(new THREE.Matrix4().makeRotationX(tilt));
  sheet.multiply(new THREE.Matrix4().makeTranslation(0, proj * 0.5 + SURFACE_GAP, 0));
  addOwned(ctx, 'awning', texturedQuad(length, proj, length / AWNING_TILE, 1), sheet);

  // Frame: a fixing band on the wall, a front bar, and a bracket at each end.
  addFaceSlab(ctx, 'rail', parent, f, du, dv + 0.06, length + 0.12, 0.1, 0.07, 0);
  const bar = faceXf(parent, f, du, dv, 0);
  bar.multiply(new THREE.Matrix4().makeRotationX(tilt));
  bar.multiply(new THREE.Matrix4().makeTranslation(0, proj + 0.02, 0));
  bar.multiply(new THREE.Matrix4().makeScale(length + 0.1, 0.07, 0.07));
  ctx.add('rail', UNIT_BOX, bar);
  // A short valance hanging off the front bar: the shape that makes an awning read as an
  // awning rather than as a coloured shelf. Same sheet, turned to hang down.
  const valance = faceXf(parent, f, du, dv, 0);
  valance.multiply(new THREE.Matrix4().makeRotationX(tilt));
  valance.multiply(new THREE.Matrix4().makeTranslation(0, proj + 0.02, 0.14));
  const vGeo = texturedQuad(length, 0.28, length / AWNING_TILE, 0.34);
  vGeo.rotateX(-Math.PI / 2);
  addOwned(ctx, 'awning', vGeo, valance);
  for (const s of [-1, 1]) {
    const a = facePoint(f, du + s * length * 0.5, dv + 0.06, _vA, 0.04);
    const b = facePoint(f, du + s * length * 0.5, dv - proj * Math.sin(tilt), _vB, proj * Math.cos(tilt));
    addTubeBetween(ctx, 'rail', parent, a.clone(), b.clone(), 0.03, true);
  }
}

/**
 * A FRAMELESS GLASS BALUSTRADE with a brushed cap rail: the pristine version of a roof edge.
 *
 * `edgeRailing` is the service-roof answer — painted steel posts, a working building. This
 * is the showpiece answer, and DESIGN.md 4 asks for both ("glass curtain walls, brushed
 * aluminium... spotless glass"). Alternating them along the arena's edges is what stops a
 * hundred edges reading as one extruded detail, and glass is the only balustrade that can
 * be full height without hiding anybody: it is the same `glass` material the curtain
 * walling uses, so you see straight through it.
 */
export function glassBalustrade(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  topDv: number,
  length: number,
  inset: number,
): void {
  if (length < 1.2) return;
  const panels = Math.max(1, Math.min(14, Math.round(length / 2.2)));
  const pw = length / panels;
  for (let i = 0; i < panels; i++) {
    const du = -length * 0.5 + pw * (i + 0.5);
    const p = facePoint(f, du, topDv, _vA, -inset);
    const m = new THREE.Matrix4().makeTranslation(p.x, p.y + BALUSTRADE_HEIGHT * 0.5, p.z);
    m.multiply(new THREE.Matrix4().makeRotationY(Math.atan2(f.n.x, f.n.z)));
    m.multiply(new THREE.Matrix4().makeScale(pw - 0.06, BALUSTRADE_HEIGHT - 0.1, 0.035));
    ctx.add('glass', UNIT_BOX, m.premultiply(parent));
    // The shoe the pane is clamped into.
    const shoe = new THREE.Matrix4().makeTranslation(p.x, p.y + 0.06, p.z);
    shoe.multiply(new THREE.Matrix4().makeRotationY(Math.atan2(f.n.x, f.n.z)));
    shoe.multiply(new THREE.Matrix4().makeScale(pw - 0.04, 0.12, 0.12));
    ctx.add('metal', UNIT_BOX, shoe.premultiply(parent));
  }
  // One continuous cap rail. This is the line that draws the edge from across the map, and
  // it is the reason a glass balustrade is legible at all against a bright sky.
  const a = facePoint(f, -length * 0.5, topDv, new THREE.Vector3(), -inset);
  const b = facePoint(f, length * 0.5, topDv, new THREE.Vector3(), -inset);
  addTubeBetween(
    ctx,
    'metal',
    parent,
    new THREE.Vector3(a.x, a.y + BALUSTRADE_HEIGHT, a.z),
    new THREE.Vector3(b.x, b.y + BALUSTRADE_HEIGHT, b.z),
    0.05,
    false,
  );
}

/**
 * A TOWER CRANE on a neighbouring roof — the skyline's one big confident colour.
 *
 * City dressing only, and that placement is what makes the yellow affordable. Sun yellow at
 * this chroma is the nearest thing in the palette to the chambered gun's amber, so it is
 * rationed to *equipment* (see `accentSun`); a crane standing a hundred and thirty metres
 * out over the city is unmistakably equipment, is never in the same depth plane as a gun a
 * player is trying to read, and gives the horizon the warm vertical accent that a white
 * city under a blue sky otherwise completely lacks. It also says the city is being *built*,
 * which is most of what makes a skyline feel like a real place rather than a backdrop.
 *
 * Skeletal by construction — legs, ties, a jib and a hoist rope. Outside the play space it
 * could not be cover in any case.
 */
export function towerCrane(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  cx: number,
  cy: number,
  cz: number,
  mastHeight: number,
  jib: number,
  facing: number,
): void {
  const id = new THREE.Matrix4();
  const frame = localXf(id, cx, cy, cz, 1, 1, 1, 0, facing, 0).premultiply(parent);
  const leg = 1.1;

  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      addBox(ctx, 'accentSun', frame, sx * leg, mastHeight * 0.5, sz * leg, 0.2, mastHeight, 0.2);
    }
  }
  const ties = Math.max(2, Math.round(mastHeight / 5));
  for (let i = 1; i <= ties; i++) {
    const y = (mastHeight / ties) * i;
    addBox(ctx, 'accentSun', frame, 0, y, 0, leg * 2 + 0.2, 0.14, leg * 2 + 0.2);
  }

  // Slewing ring, cab and the A-frame the pendants run over.
  addBox(ctx, 'rail', frame, 0, mastHeight + 0.35, 0, leg * 2.4, 0.7, leg * 2.4);
  addBox(ctx, 'panel', frame, leg * 1.6, mastHeight + 1.5, 0, 1.5, 1.6, 1.7);
  addBox(ctx, 'accentSun', frame, 0, mastHeight + 3.4, 0, 0.24, 5.0, 0.24);

  // Jib and counter-jib.
  const jibY = mastHeight + 1.5;
  addBox(ctx, 'accentSun', frame, jib * 0.5, jibY, 0, jib, 0.42, 0.42);
  const counter = jib * 0.34;
  addBox(ctx, 'accentSun', frame, -counter * 0.5, jibY, 0, counter, 0.42, 0.42);
  addBox(ctx, 'trim', frame, -counter, jibY + 0.4, 0, 2.0, 1.4, 2.2);
  // Pendant bars from the A-frame out to both jibs.
  const peak = new THREE.Vector3(0, mastHeight + 5.9, 0);
  addTubeBetween(ctx, 'rail', frame, peak, new THREE.Vector3(jib * 0.92, jibY, 0), 0.05);
  addTubeBetween(ctx, 'rail', frame, peak, new THREE.Vector3(-counter * 0.9, jibY, 0), 0.05);

  // Trolley and hoist rope: the detail that makes a crane read as working rather than parked.
  const tx = jib * 0.62;
  addBox(ctx, 'trim', frame, tx, jibY - 0.36, 0, 0.7, 0.34, 0.5);
  const drop = mastHeight * 0.42;
  addTubeBetween(
    ctx,
    'rubber',
    frame,
    new THREE.Vector3(tx, jibY - 0.5, 0),
    new THREE.Vector3(tx, jibY - 0.5 - drop, 0),
    0.025,
  );
  addBox(ctx, 'accentSun', frame, tx, jibY - 0.7 - drop, 0, 0.5, 0.5, 0.5);
}

/**
 * A timber-staved water tank on a steel frame — the most recognisable rooftop object there
 * is, and the one that most quickly says "city roof" rather than "arena".
 *
 * Placed on the surrounding city rather than in the play space, because on legs it would be
 * a large opaque volume that collision does not have. `wood` survives here, where it is
 * literally true: real tanks are cedar staves in steel hoops, and they are the one warm mass
 * in a white skyline.
 *
 * `stave` lets the caller paint one: a run of neighbouring roofs where one tank is bare
 * cedar and the next is painted terracotta is a *city*, and a ring of eleven identical pale
 * cylinders is wallpaper. It is also the cheapest saturated colour in the whole arena —
 * two metres across, a hundred and thirty metres out, and behind a parapet from most of
 * the play space.
 */
export function waterTank(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  cx: number,
  cy: number,
  cz: number,
  radius: number,
  height: number,
  stave: MatKey = 'wood',
): void {
  const legH = height * 0.45;
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI * 0.25;
    const x = cx + Math.cos(a) * radius * 0.78;
    const z = cz + Math.sin(a) * radius * 0.78;
    addCyl(ctx, 'rail', parent, x, cy + legH * 0.5, z, radius * 0.07, legH, 0, 0, 0, true);
  }
  addCyl(ctx, stave, parent, cx, cy + legH + height * 0.5, cz, radius, height);
  for (const t of [0.18, 0.5, 0.82]) {
    addCyl(ctx, 'metalDark', parent, cx, cy + legH + height * t, cz, radius * 1.04, height * 0.05);
  }
  ctx.add(
    'metalDark',
    UNIT_CONE,
    localXf(parent, cx, cy + legH + height + radius * 0.3, cz, radius * 2.1, radius * 0.62, radius * 2.1),
  );
}

/**
 * A rooftop plant room / stair core: a boxy white structure with a louvre band and its own
 * parapet. City dressing only — never in the play space, for the same reason as the tank.
 *
 * `bulkhead` paints the whole box. A painted stair core is the canonical Mirror's Edge
 * landmark, and out in the city it is free: it is a big confident block of colour a hundred
 * and thirty metres away, in a hue no team owns, that gives the eye somewhere to land in a
 * frame that is otherwise white boxes and blue sky. Neutral by default.
 */
export function plantRoom(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  cx: number,
  cy: number,
  cz: number,
  w: number,
  h: number,
  d: number,
  bulkhead: MatKey = 'panel',
): void {
  addBox(ctx, bulkhead, parent, cx, cy + h * 0.5, cz, w, h, d);
  addBox(ctx, 'metal', parent, cx, cy + h * 0.62, cz, w + 0.06, h * 0.22, d + 0.06);
  addBox(ctx, 'trim', parent, cx, cy + h + 0.06, cz, w + 0.24, 0.12, d + 0.24);
}

/**
 * A TALL SLIM STEEL SECTION: an antenna mast, an aerial stem, a lift overrun.
 *
 * This is the skin a rooftop mast gets, and it replaces the `serverRack` that used to land
 * on these brushes. That mattered more than the count suggests: on this arena the tall slim
 * metal brushes are the antenna masts, including the one on the helipad, which is the
 * tallest object in the level and the thing every player orients off. A rack of relay units
 * with drive slots in it is *interior* vocabulary — it says data centre, and a data centre
 * standing on an open roof is the exact category error DESIGN.md 4 calls out.
 *
 * The shell stays a solid box, because that is what collision has: a see-through lattice on
 * a solid brush is a lie in the other direction, where a shot at a visible enemy stops in
 * mid-air. The lattice read comes from applied steel instead — corner angles proud by
 * `SURFACE_RELIEF`, collar bands at a fixed 2.4 m rhythm, and X-bracing across every bay.
 * At distance those resolve into a trussed tower; up close they are a few centimetres of
 * relief on a mast you can neither see nor shoot through, which is exactly the truth.
 */
export function latticeMast(ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
  shell(ctx, parent, half, o.base);
  const { x: hx, y: hy, z: hz } = half;

  // Corner angles, sitting exactly SURFACE_RELIEF proud of the shell.
  const post = Math.min(0.2, Math.min(hx, hz) * 0.8);
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      addBox(
        ctx,
        'rail',
        parent,
        sx * (hx - post * 0.5 + SURFACE_RELIEF),
        0,
        sz * (hz - post * 0.5 + SURFACE_RELIEF),
        post,
        hy * 2 - 0.08,
        post,
      );
    }
  }

  // Collar bands. A fixed metre pitch is what gives a mast its scale from far away — it is
  // the same trick the handrail posts play on a long parapet.
  const bays = Math.max(1, Math.min(9, Math.round((hy * 2) / 2.4)));
  const step = (hy * 2) / bays;
  for (let i = 0; i <= bays; i++) {
    const y = -hy + step * i;
    addBox(ctx, 'metal', parent, 0, Math.min(hy - 0.06, Math.max(-hy + 0.06, y)), 0, hx * 2 + SURFACE_RELIEF * 2, 0.11, hz * 2 + SURFACE_RELIEF * 2);
  }

  // X-bracing across each bay of each exposed face.
  for (const f of boxFaces(half)) {
    if (!isVerticalFace(f) || !o.exposed[f.index]) continue;
    const w = f.w - post * 2;
    if (w < 0.3) continue;
    for (let i = 0; i < bays; i++) {
      const y0 = -hy + step * i + 0.09;
      const y1 = y0 + step - 0.18;
      if (y1 <= y0) continue;
      for (const s of [-1, 1]) {
        addTubeBetween(
          ctx,
          'rail',
          parent,
          facePoint(f, s * w * 0.5, y0, new THREE.Vector3(), SURFACE_RELIEF * 0.7),
          facePoint(f, -s * w * 0.5, y1, new THREE.Vector3(), SURFACE_RELIEF * 0.7),
          0.045,
        );
      }
    }
  }

  // Head plate. The mast has to end in something, or it reads as a section cut off.
  addBox(ctx, 'panel', parent, 0, hy - 0.09, 0, hx * 2 + 0.18, 0.18, hz * 2 + 0.18);
}

/**
 * A ROOFTOP PLANT ENCLOSURE: the low boxy thing on every real roof that has a fan in it.
 *
 * Replaces `crateStack` and `vendingBank` on the small concrete blocks of an open deck.
 * Both of those were interior-and-warehouse vocabulary — a stencilled bay number on a
 * packing crate, a lit dispenser cabinet behind glass — and a roof has neither. What a roof
 * has is a rendered enclosure with a coping on it, a louvre band on the face the plant
 * breathes through, and a kerb at its foot where it meets the membrane.
 */
export function plantBox(ctx: BuildCtx, parent: THREE.Matrix4, half: THREE.Vector3, o: SkinOpts): void {
  shell(ctx, parent, half, o.base);
  // Coping and base kerb, both kept inside the collision shell in Y so nothing is standable
  // that the simulation does not know about.
  addBox(ctx, 'metal', parent, 0, half.y - 0.06, 0, half.x * 2 + 0.14, 0.12, half.z * 2 + 0.14);
  addBox(ctx, 'trim', parent, 0, -half.y + 0.08, 0, half.x * 2 + 0.07, 0.16, half.z * 2 + 0.07);

  let vents = 0;
  for (const f of boxFaces(half)) {
    if (!isVerticalFace(f) || !o.exposed[f.index]) continue;
    if (f.w < 0.6 || f.h < 0.6) continue;
    if (vents < 2 && f.w > 0.95 && f.h > 0.85) {
      louvreFace(ctx, parent, f, 0, f.h * 0.06, f.w - 0.32, Math.min(f.h * 0.52, 1.1));
      vents++;
    }
    boltRow(ctx, parent, f, -f.w * 0.5 + 0.12, 2);
    boltRow(ctx, parent, f, f.w * 0.5 - 0.12, 2);
  }
}

/**
 * THE THING A GRAPPLE ANCHOR HANGS OFF.
 *
 * DESIGN.md 4 asks for grapple points that read as *believable rooftop hardware*, and an
 * anchor fixture on its own does not get there: on this arena fifteen of the nineteen
 * anchors sit between twelve and twenty-one metres above the nearest roof, so what the
 * player saw was a piece of rigging floating in the sky with nothing holding it up. An
 * unexplained object hanging in mid-air reads as a *pickup*, which is the one thing an
 * anchor must never read as — and no amount of silhouette work on the fixture itself fixes
 * it, because the question the eye is asking is "what is that attached to".
 *
 * So each anchor gets a crane: a trussed mast standing on the nearest roof, a lattice jib
 * cantilevering out to the hook, a pendant stay from the mast head to the jib nose, and a
 * counterweight over the mast base. That structure answers the question, it is a shape a
 * roofscape of decks and parapets contains nowhere else, and it points at the anchor —
 * a jib is an arrow with the hook on the end of it.
 *
 * Skeletal by construction: every member is a 4–9 cm tube and the mast is 0.9 m across, so
 * it hides nobody and blocks no shot. It is the same class of object as the cable poles in
 * `buildOverhead`, which also stand on roofs collision does not know about, and it is held
 * to the same rule — nothing here is ever wide enough to be mistaken for cover.
 *
 * `beamDrop` is how far the fixture's own beam sits above the hook, so the jib meets the
 * fixture instead of passing through it.
 */
export function anchorRig(
  ctx: BuildCtx,
  root: THREE.Vector3,
  anchor: THREE.Vector3,
  beamDrop: number,
): void {
  const beamY = anchor.y + beamDrop;
  const height = beamY - root.y;
  if (height < 2.0) return;

  const dx = anchor.x - root.x;
  const dz = anchor.z - root.z;
  const reach = Math.hypot(dx, dz);
  // Frame whose +Z points along the jib, so the truss can be authored in one straight run.
  const parent = localXf(new THREE.Matrix4(), root.x, root.y, root.z, 1, 1, 1, 0, reach > 1e-3 ? Math.atan2(dx, dz) : 0, 0);

  const leg = 0.45;
  const corners: readonly (readonly [number, number])[] = [
    [-leg, -leg],
    [leg, -leg],
    [leg, leg],
    [-leg, leg],
  ];

  // Base: a painted pad and a machined sole plate, so the mast lands on the roof rather
  // than growing out of it.
  addBox(ctx, 'trim', parent, 0, 0.05, 0, leg * 2 + 0.8, 0.1, leg * 2 + 0.8);
  addBox(ctx, 'rail', parent, 0, 0.18, 0, leg * 2 + 0.45, 0.16, leg * 2 + 0.45);

  const foot = 0.26;
  const shaft = height - foot;
  for (const [lx, lz] of corners) {
    addCyl(ctx, 'rail', parent, lx, foot + shaft * 0.5, lz, 0.08, shaft, 0, 0, 0, true);
  }
  const bays = Math.max(1, Math.min(12, Math.round(shaft / 2.6)));
  for (let i = 0; i <= bays; i++) {
    const y = foot + (shaft * i) / bays;
    for (let k = 0; k < 4; k++) {
      const a = corners[k];
      const b = corners[(k + 1) % 4];
      addTubeBetween(ctx, 'rail', parent, V(a[0], y, a[1]), V(b[0], y, b[1]), 0.05);
    }
  }
  for (let i = 0; i < bays; i++) {
    const y0 = foot + (shaft * i) / bays;
    const y1 = foot + (shaft * (i + 1)) / bays;
    for (let k = 0; k < 4; k++) {
      const a = corners[k];
      const b = corners[(k + 1) % 4];
      addTubeBetween(ctx, 'rail', parent, V(a[0], y0, a[1]), V(b[0], y1, b[1]), 0.042);
    }
  }

  if (reach <= 1.2) {
    // The anchor is directly over the mast: cap it, and let the fixture be the head.
    addBox(ctx, 'panel', parent, 0, height - 0.13, 0, leg * 2 + 0.5, 0.26, leg * 2 + 0.5);
    return;
  }

  // THE JIB. Two chords a metre apart, tapering toward the nose, with posts and diagonals
  // between them — the standard shape of a tower-crane boom, and the reason this fixture
  // can be recognised as machinery from four hundred metres.
  const top = height;
  const bot = height - 0.95;
  const panels = Math.max(2, Math.min(12, Math.round(reach / 2.2)));
  const halfW = (t: number): number => 0.42 - 0.24 * t;
  for (const s of [-1, 1]) {
    addTubeBetween(ctx, 'rail', parent, V(s * halfW(0), top, 0), V(s * halfW(1), top, reach), 0.075);
    addTubeBetween(ctx, 'rail', parent, V(s * halfW(0), bot, 0), V(s * halfW(1), bot, reach), 0.06);
  }
  for (let i = 0; i <= panels; i++) {
    const t = i / panels;
    const w = halfW(t);
    const z = reach * t;
    for (const s of [-1, 1]) addTubeBetween(ctx, 'rail', parent, V(s * w, top, z), V(s * w, bot, z), 0.045);
    addTubeBetween(ctx, 'rail', parent, V(-w, top, z), V(w, top, z), 0.045);
  }
  for (let i = 0; i < panels; i++) {
    const t0 = i / panels;
    const t1 = (i + 1) / panels;
    for (const s of [-1, 1]) {
      addTubeBetween(ctx, 'rail', parent, V(s * halfW(t0), bot, reach * t0), V(s * halfW(t1), top, reach * t1), 0.04);
    }
  }

  // The A-frame head and its two pendants: one forward to the jib nose, one back to the
  // counterweight. This triangle is what makes the whole thing read as a crane rather than
  // as a gantry, and it is visible in silhouette long after the truss members have washed out.
  const headH = Math.min(3.4, Math.max(1.8, reach * 0.35));
  addCyl(ctx, 'rail', parent, 0, top + headH * 0.5, 0, 0.09, headH);
  addBox(ctx, 'panel', parent, 0, top + headH + 0.1, 0, 0.5, 0.2, 0.5);
  addTubeBetween(ctx, 'rail', parent, V(0, top + headH, 0), V(0, top, reach * 0.92), 0.045);

  const tail = Math.min(3.2, reach * 0.45 + 1.0);
  for (const s of [-1, 1]) {
    addTubeBetween(ctx, 'rail', parent, V(s * 0.42, top, 0), V(s * 0.3, top, -tail), 0.06);
    addTubeBetween(ctx, 'rail', parent, V(s * 0.42, bot, 0), V(s * 0.3, bot, -tail), 0.05);
  }
  addTubeBetween(ctx, 'rail', parent, V(0, top + headH, 0), V(0, top, -tail * 0.95), 0.045);
  // The counterweight: the one solid block in the assembly, and white, so the crane keeps a
  // bright note against a shaded tower face as well as a dark one against open sky.
  addBox(ctx, 'panel', parent, 0, bot - 0.42, -tail, 1.5, 1.05, 0.9);
  addBox(ctx, 'trim', parent, 0, bot - 0.42, -tail, 1.58, 0.18, 0.96);
}

/**
 * THE SPAWN BANNER. A flag mast with a hanging banner in the team's colour.
 *
 * This exists to replace a glowing decal painted on the ground. DESIGN.md 4 is explicit
 * that if the most saturated element in a frame is on the floor, the colour budget has been
 * spent in the wrong place — and a spawn is exactly where the temptation to do that is
 * strongest, because the marker has to be found in half a second.
 *
 * A banner solves it better than paint ever did. It is *vertical*, so it is visible from
 * across the map instead of only from directly above; it silhouettes against open sky,
 * which is now the background of everything; and it puts the team's saturated colour up in
 * the air where it competes with nothing a player has to shoot. The mast is 8 cm of steel
 * and the banner is a 4 cm sheet, so the whole assembly hides nobody.
 *
 * Returns the two board faces so the caller can stencil the team name on them.
 */
export function bannerMast(
  ctx: BuildCtx,
  centre: THREE.Vector3,
  height: number,
  facing: number,
  field: MatKey,
  band: MatKey,
): { pos: THREE.Vector3; normal: THREE.Vector3 }[] {
  const id = new THREE.Matrix4();
  const parent = localXf(id, centre.x, centre.y, centre.z, 1, 1, 1, 0, facing, 0);

  // Base plate and mast. Everything below is authored in a frame whose +Z faces `facing`.
  addBox(ctx, 'trim', parent, 0, 0.05, 0, 0.72, 0.1, 0.72);
  addBox(ctx, 'rail', parent, 0, 0.16, 0, 0.5, 0.12, 0.5);
  addCyl(ctx, 'rail', parent, 0, height * 0.5, 0, 0.055, height);

  // Cross arm the banner hangs from, and a finial so the mast ends in something.
  const bw = 1.5;
  const bh = Math.min(height * 0.52, 3.0);
  const armY = height - 0.24;
  addBox(ctx, 'rail', parent, 0, armY, 0, bw + 0.4, 0.075, 0.075);
  addSphere(ctx, 'metal', parent, 0, height + 0.12, 0, 0.09);

  // The banner: a pale tinted field with full-chroma head and foot bands. Same reasoning as
  // the team walls — a hundred square metres of signal red is a wall a red player cannot be
  // seen against, and the saturated statement is stronger made as a band on a pale field.
  const cy = armY - bh * 0.5 - 0.12;
  addBox(ctx, field, parent, 0, cy, 0, bw, bh, 0.035);
  for (const s of [-1, 1]) {
    addBox(ctx, band, parent, 0, cy + s * (bh * 0.5 - 0.16), 0, bw + 0.02, 0.32, 0.05);
  }
  addBox(ctx, 'trim', parent, 0, cy, 0, bw + 0.09, bh + 0.09, 0.02);

  const out: { pos: THREE.Vector3; normal: THREE.Vector3 }[] = [];
  for (const s of [1, -1]) {
    out.push({
      pos: new THREE.Vector3(0, cy, s * 0.06).applyMatrix4(parent),
      normal: new THREE.Vector3(0, 0, s).transformDirection(parent),
    });
  }
  return out;
}

/**
 * A low planting bed run along the foot of a wall: a pale kerb with clipped greenery in it.
 *
 * Planting is on DESIGN.md's material list and it earns its place for a reason that is not
 * decorative — it is the only thing in the vocabulary that puts a *soft*, irregular
 * silhouette into a building made entirely of straight lines, which is what keeps a white
 * box from reading as a greybox.
 *
 * Two greens, not one. The sides are `foliage` and the sunlit tops are `foliageLight`,
 * because a clipped box shades identically on its top and its sides under a toon ramp — so
 * the light has to be painted in or the bed lands as one flat dark mass. On a map whose
 * brief is "a beautiful day, insanely pristine" that difference is most of the point.
 *
 * `PLANTER_HEIGHT` is below `STEP_HEIGHT`, so this cannot conceal or obstruct anything.
 */
export function plantingBed(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  baseDv: number,
  length: number,
): void {
  if (length < 1.0) return;
  const d = PLANTER_DEPTH;
  const h = PLANTER_HEIGHT;
  // Kerb: a shallow pale trough standing off the wall, spanning [0, d] outward from it.
  const m = faceXf(parent, f, du, baseDv + h * 0.5, d * 0.5);
  m.multiply(new THREE.Matrix4().makeScale(length, d, h));
  ctx.add('panel', UNIT_BOX, m);
  const lip = faceXf(parent, f, du, baseDv + h, d * 0.5);
  lip.multiply(new THREE.Matrix4().makeScale(length + 0.06, d + 0.06, 0.05));
  ctx.add('trim', UNIT_BOX, lip);

  /**
   * Clipped planting: a run of slightly staggered blocks, so the top line is not a ruler.
   *
   * **Clamped so the whole bed tops out around half a metre.** It used to reach 0.48 m of
   * greenery on a 0.22 m kerb — 0.70 m above the deck, on a brush collision does not have.
   * That cannot hide a crouched head (0.98 m) but it *can* hide a gun lying on the ground,
   * which is 0.44 m across and is the most important object in the game to be able to find.
   * A bed that swallows a dropped gun is a bed that decides rounds. Same discipline as
   * `roofPlanter`, whose clumps were already inside it.
   */
  const clumps = Math.max(2, Math.min(14, Math.round(length / 0.9)));
  for (let i = 0; i < clumps; i++) {
    const cu = -length * 0.5 + (length / clumps) * (i + 0.5);
    const ch = 0.16 + ctx.rng() * 0.14;
    const cw = (length / clumps) * (0.72 + ctx.rng() * 0.2);
    const g = faceXf(parent, f, du + cu, baseDv + h + ch * 0.5 - 0.04, d * 0.48);
    g.multiply(new THREE.Matrix4().makeScale(cw, d * 0.78, ch));
    ctx.add('foliage', UNIT_BOX, g);
    // 2 cm proud of the clump's own top rather than flush with it: two coplanar faces fight
    // in the depth pass, and the symptom is half the planting flickering between two greens.
    const cap = faceXf(parent, f, du + cu, baseDv + h + ch - 0.05, d * 0.48);
    cap.multiply(new THREE.Matrix4().makeScale(cw * 0.9, d * 0.68, 0.06));
    ctx.add('foliageLight', UNIT_BOX, cap);
  }
}

/**
 * Coffer ribs on the underside of a deck.
 *
 * The prior review's finding was that the ceiling "reads as a HOLE in the render, not a
 * roof": a single flat plane at the darkest rung of the value ladder, with no thickness,
 * no direction and no scale. None of that is fixable with a brighter grey — an unbroken
 * plane of *any* value reads as absence. What fixes it is structure: downstand beams throw
 * the ribs into the lit band and the bays between them into shade, which gives a soffit a
 * direction, a repeat you can measure the room against, and an edge where it meets the wall.
 */
export function soffitCoffers(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  bay: number,
  depth: number,
): void {
  const w = f.w - FLOOR_INSET * 2;
  const h = f.h - FLOOR_INSET * 2;
  if (w < bay || h < 0.8) return;
  // Clear of the soffit lining plate the caller has already laid, so the two never share a
  // plane — coplanar surfaces fight in the depth pass and half the ceiling goes black.
  const lift = SOFFIT_LINING_THICKNESS + SURFACE_GAP;
  const ribs = Math.min(16, Math.floor(w / bay));
  const cross = Math.min(16, Math.floor(h / bay));
  for (let i = 1; i < ribs; i++) {
    addFaceSlab(ctx, 'panel', parent, f, -w * 0.5 + (w / ribs) * i, 0, 0.24, h, depth, lift);
  }
  for (let j = 1; j < cross; j++) {
    addFaceSlab(ctx, 'panel', parent, f, 0, -h * 0.5 + (h / cross) * j, w, 0.24, depth * 0.6, lift);
  }
  // Perimeter downstand, so the soffit has a visible thickness where it meets the wall.
  // The prior review's "no thickness" finding is literally this edge being absent.
  addFaceSlab(ctx, 'panel', parent, f, 0, h * 0.5, w, 0.32, depth, lift);
  addFaceSlab(ctx, 'panel', parent, f, 0, -h * 0.5, w, 0.32, depth, lift);
  addFaceSlab(ctx, 'panel', parent, f, -w * 0.5, 0, 0.32, h, depth, lift);
  addFaceSlab(ctx, 'panel', parent, f, w * 0.5, 0, 0.32, h, depth, lift);
}

/**
 * A skylight let into a soffit: a bright glazed panel in a machined frame.
 *
 * Sun-drenched is a *contrast* claim, not a brightness claim — a frame is only sunlit if
 * something in it is the sun. Punching daylight openings through the roof puts the
 * brightest value in the building overhead, where the eye expects light to come from, and
 * it is the second half of the fix for the black-slab ceiling: ribs give the soffit form,
 * skylights give it a reason to be looked at.
 */
export function skylightPanel(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  w: number,
  h: number,
): void {
  if (w < 0.6 || h < 0.6) return;
  const frame = Math.min(0.18, Math.min(w, h) * 0.12);
  const lift = SOFFIT_LINING_THICKNESS + SURFACE_GAP;
  // The light itself, then a picture frame around it — NOT a plate under it, or the metal
  // would simply cover the glazing up.
  addFaceSlab(ctx, 'skylight', parent, f, du, dv, w - frame * 2, h - frame * 2, 0.07, lift);
  addFaceSlab(ctx, 'metal', parent, f, du, dv + (h - frame) * 0.5, w, frame, 0.13, lift);
  addFaceSlab(ctx, 'metal', parent, f, du, dv - (h - frame) * 0.5, w, frame, 0.13, lift);
  addFaceSlab(ctx, 'metal', parent, f, du - (w - frame) * 0.5, dv, frame, h - frame * 2, 0.13, lift);
  addFaceSlab(ctx, 'metal', parent, f, du + (w - frame) * 0.5, dv, frame, h - frame * 2, 0.13, lift);

  // Glazing bars across the light. Without them a skylight is a white rectangle; with
  // them it is a roof light.
  const along = w >= h;
  const span = along ? w : h;
  const bars = Math.max(1, Math.min(6, Math.round(span / 1.4)));
  for (let i = 1; i < bars; i++) {
    const t = -span * 0.5 + (span / bars) * i;
    addFaceSlab(
      ctx,
      'metal',
      parent,
      f,
      du + (along ? t : 0),
      dv + (along ? 0 : t),
      along ? 0.08 : w - frame * 2,
      along ? h - frame * 2 : 0.08,
      0.04,
      lift + 0.075,
    );
  }
}

/**
 * A rectangular service duct hung under a soffit, with hanger rods and joint ribs.
 *
 * Plant and ductwork is what makes a ceiling read as the underside of a *building*. It is
 * only ever hung from a soffit the caller has already proved is high above the deck below,
 * and it is shallow, so it can never be mistaken for something to hide behind — it is
 * above every head in the game by construction.
 */
export function hangingDuct(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  f: Face,
  du: number,
  dv: number,
  length: number,
  size: number,
  along: boolean,
): void {
  if (length < 2) return;
  const drop = size * 0.9 + 0.24;
  const m = faceXf(parent, f, du, dv, drop);
  m.multiply(new THREE.Matrix4().makeScale(along ? length : size, size, along ? size : length));
  ctx.add('metal', UNIT_BOX, m);

  const joints = Math.max(2, Math.min(14, Math.round(length / 2.6)));
  for (let i = 0; i < joints; i++) {
    const t = -length * 0.5 + (length / (joints - 1)) * i;
    const j = faceXf(parent, f, du + (along ? t : 0), dv + (along ? 0 : t), drop);
    j.multiply(new THREE.Matrix4().makeScale(along ? 0.09 : size + 0.07, size + 0.07, along ? size + 0.07 : 0.09));
    ctx.add('metalDark', UNIT_BOX, j);
    // Hanger rods back up to the soffit.
    const p = facePoint(f, du + (along ? t : 0), dv + (along ? 0 : t), _vA, drop * 0.5);
    addCyl(ctx, 'metalDark', parent, p.x, p.y, p.z, 0.018, drop, ...faceRot(f));
  }
}

/**
 * Skyline clutter for the roof of a tall structure: masts, a dish, guy wires and a
 * beacon. Everything is skeletal — nothing wide enough to mistake for cover.
 */
export function antennaCluster(
  ctx: BuildCtx,
  parent: THREE.Matrix4,
  half: THREE.Vector3,
  height: number,
  cap: MatKey,
): void {
  const top = half.y;
  const masts = 2 + ((ctx.rng() * 3) | 0);
  const tips: THREE.Vector3[] = [];
  for (let i = 0; i < masts; i++) {
    const dx = (ctx.rng() - 0.5) * half.x * 1.4;
    const dz = (ctx.rng() - 0.5) * half.z * 1.4;
    const hh = height * (0.4 + ctx.rng() * 0.6);
    addCyl(ctx, 'metalDark', parent, dx, top + hh * 0.5, dz, 0.055, hh);
    const spars = 1 + ((ctx.rng() * 3) | 0);
    for (let s = 0; s < spars; s++) {
      const y = top + hh * (0.35 + s * 0.2);
      const len = 0.35 + ctx.rng() * 0.5;
      addBox(ctx, 'metalDark', parent, dx, y, dz, len, 0.035, 0.035, 0, ctx.rng() * Math.PI, 0);
    }
    tips.push(new THREE.Vector3(dx, top + hh, dz));
    if (i === 0) addSphere(ctx, cap, parent, dx, top + hh + 0.12, dz, 0.12);
  }
  for (const tip of tips) {
    const ax = (tip.x >= 0 ? 1 : -1) * half.x * 0.9;
    const az = (tip.z >= 0 ? 1 : -1) * half.z * 0.9;
    addTubeBetween(ctx, 'rubber', parent, tip, new THREE.Vector3(ax, top, az), 0.014);
  }
  if (ctx.rng() < 0.65) {
    const dx = (ctx.rng() - 0.5) * half.x;
    const dz = (ctx.rng() - 0.5) * half.z;
    addCyl(ctx, 'metalDark', parent, dx, top + 0.35, dz, 0.05, 0.7);
    ctx.add(
      'metal',
      UNIT_CONE,
      localXf(parent, dx, top + 0.78, dz, 0.9, 0.36, 0.9, -0.9 + ctx.rng() * 0.5, ctx.rng() * Math.PI * 2, 0),
    );
  }
  const stacks = 1 + ((ctx.rng() * 2) | 0);
  for (let i = 0; i < stacks; i++) {
    const dx = (ctx.rng() - 0.5) * half.x * 1.5;
    const dz = (ctx.rng() - 0.5) * half.z * 1.5;
    const hh = 0.5 + ctx.rng() * 0.7;
    addCyl(ctx, 'metal', parent, dx, top + hh * 0.5, dz, 0.16, hh);
    addCyl(ctx, 'trim', parent, dx, top + hh + 0.045, dz, 0.2, 0.09);
  }
}

/** A ring painted on a floor. A couple of centimetres of paint on the slab, not a lip. */
export function groundRing(
  ctx: BuildCtx,
  center: THREE.Vector3,
  radius: number,
  width: number,
  mat: MatKey,
  segments = 40,
): void {
  const id = new THREE.Matrix4();
  for (let i = 0; i < segments; i++) {
    const a0 = (i / segments) * Math.PI * 2;
    const a1 = ((i + 1) / segments) * Math.PI * 2;
    const x0 = center.x + Math.cos(a0) * radius;
    const z0 = center.z + Math.sin(a0) * radius;
    const x1 = center.x + Math.cos(a1) * radius;
    const z1 = center.z + Math.sin(a1) * radius;
    const len = Math.hypot(x1 - x0, z1 - z0) * 1.08;
    const ang = Math.atan2(z1 - z0, x1 - x0);
    ctx.add(
      mat,
      UNIT_BOX,
      localXf(id, (x0 + x1) * 0.5, center.y + FLOOR_TOP + 0.01, (z0 + z1) * 0.5, len, 0.02, width, 0, -ang, 0),
    );
  }
}

/** A painted bar between two points: floor lane lines and deck edge bands. */
export function paintedBar(
  ctx: BuildCtx,
  a: THREE.Vector3,
  b: THREE.Vector3,
  width: number,
  thickness: number,
  mat: MatKey,
): void {
  const id = new THREE.Matrix4();
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-4) return;
  ctx.add(
    mat,
    UNIT_BOX,
    localXf(
      id,
      (a.x + b.x) * 0.5,
      (a.y + b.y) * 0.5,
      (a.z + b.z) * 0.5,
      len,
      thickness,
      width,
      0,
      -Math.atan2(dz, dx),
      0,
    ),
  );
}

/** How hazed a city block is: 0 = the roofs next door, 2 = the far horizon. */
export type SkylineTier = 0 | 1 | 2;

const SKYLINE_MATS: readonly MatKey[] = ['skylineNear', 'skyline', 'skylineFar'];

/**
 * A building in the surrounding city. Placed outside the arena bounds — never in play.
 *
 * **This is most of the feeling.** The arena is a rooftop, and a rooftop is only a rooftop
 * if there is a city under it and a horizon past it. Three tiers of aerial perspective do
 * the depth: `tier` 0 is the ring of roofs immediately beyond the play space, close enough
 * that you read parapets, plant rooms and water tanks on them and close enough to look
 * *down* onto — which is the single strongest cue that the player is high up. Tiers 1 and 2
 * wash progressively toward the sky colour until the far horizon is barely more than a tint.
 *
 * It is the middle of a bright day, so nothing here is lit from within: the glazing bands
 * are sky caught on glass, and every mass is a flat unlit tone.
 */
export function skylineBlock(
  ctx: BuildCtx,
  center: THREE.Vector3,
  w: number,
  h: number,
  d: number,
  rot: number,
  tier: SkylineTier,
): void {
  const mass: MatKey = SKYLINE_MATS[tier];
  const id = new THREE.Matrix4();
  const parent = localXf(id, center.x, center.y + h * 0.5, center.z, 1, 1, 1, 0, rot, 0);
  addBox(ctx, mass, parent, 0, 0, 0, w, h, d);

  const bands = Math.max(2, Math.min(14, Math.floor(h / 6)));
  const bandH = h / bands - 0.6;
  if (bandH > 0.4) {
    for (let i = 0; i < bands; i++) {
      const y = -h * 0.5 + (h / bands) * (i + 0.5);
      addOwned(
        ctx,
        'skylineGlass',
        texturedQuad(w * 0.94, bandH, Math.max(1, Math.round(w / 3)), 1),
        localXf(parent, 0, y, d * 0.5 + 0.06, 1, 1, 1, 0, 0, 0),
      );
      addOwned(
        ctx,
        'skylineGlass',
        texturedQuad(d * 0.94, bandH, Math.max(1, Math.round(d / 3)), 1),
        localXf(parent, w * 0.5 + 0.06, y, 0, 1, 1, 1, 0, Math.PI / 2, 0),
      );
    }
  }

  // Parapet, and the roof deck itself. On a near block the roof is a *place* — the player
  // is looking straight down at it — so it gets a real membrane field and real plant.
  const top = h * 0.5;
  addBox(ctx, mass, parent, 0, top + 0.55, 0, w + 0.3, 1.1, d + 0.3);
  addBox(ctx, 'trim', parent, 0, top + 1.14, 0, w + 0.42, 0.14, d + 0.42);

  if (tier === 0) {
    addOwned(
      ctx,
      'membrane',
      texturedFloorQuad(w, d, w / MEMBRANE_TILE, d / MEMBRANE_TILE),
      localXf(parent, 0, top + 0.03, 0, 1, 1, 1, 0, 0, 0),
    );
    /**
     * COLOUR IN THE CITY — the cheapest splash in the game, and most of the answer to
     * "it still feels a little muted".
     *
     * A third of the frame in a typical screenshot is the neighbouring roofs, and until now
     * every object on every one of them was white, pale grey or graphite. One painted stair
     * core, one terracotta tank and the occasional crane per few roofs turns a monochrome
     * backdrop into a city — and it is *free*: these are a hundred and thirty metres out,
     * on the far side of a parapet, in a hue no team owns, so they can never eat a player's
     * silhouette or compete with a chambered gun for the eye.
     *
     * The odds are the whole budget. Roughly one roof in three carries a painted bulkhead,
     * one in three a coloured tank, one in six a crane: enough that the city has colour in
     * it, sparse enough that any given block is still mostly white.
     */
    if (Math.min(w, d) > 9) {
      const rw = w * (0.2 + ctx.rng() * 0.16);
      const rd = d * (0.2 + ctx.rng() * 0.16);
      const roll = ctx.rng();
      const bulkhead: MatKey = roll < 0.17 ? 'accentClay' : roll < 0.34 ? 'accentJade' : 'panel';
      plantRoom(
        ctx,
        parent,
        (ctx.rng() - 0.5) * (w - rw) * 0.7,
        top,
        (ctx.rng() - 0.5) * (d - rd) * 0.7,
        rw,
        2.4 + ctx.rng() * 2.4,
        rd,
        bulkhead,
      );
    }
    if (ctx.rng() < 0.55) {
      const tr = 1.1 + ctx.rng() * 0.7;
      waterTank(
        ctx,
        parent,
        (ctx.rng() - 0.5) * (w - tr * 3),
        top,
        (ctx.rng() - 0.5) * (d - tr * 3),
        tr,
        2.2 + ctx.rng() * 1.2,
        ctx.rng() < 0.34 ? 'accentClay' : 'wood',
      );
    }
    // A tower crane every sixth roof or so. This is the one object in the arena allowed to
    // be a big confident yellow, and it is the strongest single thing the skyline can do
    // for "a beautiful day in a city that is being built".
    if (Math.min(w, d) > 12 && ctx.rng() < 0.17) {
      towerCrane(
        ctx,
        parent,
        (ctx.rng() - 0.5) * (w - 8),
        top,
        (ctx.rng() - 0.5) * (d - 8),
        26 + ctx.rng() * 30,
        20 + ctx.rng() * 16,
        ctx.rng() * Math.PI * 2,
      );
    }
  } else {
    addBox(ctx, mass, parent, 0, top + 1.7, 0, w * 0.4, 1.2, d * 0.4);
  }

  const mh = 3 + ctx.rng() * 7;
  addCyl(ctx, mass, parent, 0, top + 1.2 + mh * 0.5, 0, 0.14, mh);
}

// ---------------------------------------------------------------------------
// Textured geometry
// ---------------------------------------------------------------------------

/** A quad in the XY plane facing +Z, with UV repeat baked into the attribute. */
export function texturedQuad(w: number, h: number, ru: number, rv: number): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  const hw = w * 0.5;
  const hh = h * 0.5;
  geo.setAttribute(
    'position',
    new THREE.Float32BufferAttribute([-hw, -hh, 0, hw, -hh, 0, hw, hh, 0, -hw, -hh, 0, hw, hh, 0, -hw, hh, 0], 3),
  );
  geo.setAttribute(
    'normal',
    new THREE.Float32BufferAttribute([0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1], 3),
  );
  geo.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, ru, 0, ru, rv, 0, 0, ru, rv, 0, rv], 2));
  return geo;
}

/** A horizontal quad facing +Y, for hazard bands painted on ledges. */
export function texturedFloorQuad(w: number, d: number, ru: number, rv: number): THREE.BufferGeometry {
  const geo = texturedQuad(w, d, ru, rv);
  geo.rotateX(-Math.PI / 2);
  return geo;
}

// ---------------------------------------------------------------------------
// Tiling textures
// ---------------------------------------------------------------------------

function canvas2d(w: number, h: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d');
  if (!g) throw new Error('props: 2D canvas context unavailable');
  return { c, g };
}

const hex = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

function finishTiling(c: HTMLCanvasElement, aniso = 8): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = aniso;
  tex.needsUpdate = true;
  return tex;
}

/** Hard N-band ramp for MeshToonMaterial, matching the renderer's toon look. */
export function makeToonGradient(bands = TOON_BANDS): THREE.DataTexture {
  const n = Math.max(2, bands);
  const data = new Uint8Array(n * 4);
  for (let i = 0; i < n; i++) {
    // The darkest band is lifted: unreadable shadows lose fights.
    const t = 0.34 + (i / (n - 1)) * 0.66;
    const v = Math.round(t * 255);
    data[i * 4 + 0] = v;
    data[i * 4 + 1] = v;
    data[i * 4 + 2] = v;
    data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, n, 1, THREE.RGBAFormat);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Expanded-metal grating. Used with alphaTest (never blending) so it stays out of
 * the transparency sort and can't produce order-dependent artefacts.
 */
export function makeGrateTexture(): THREE.CanvasTexture {
  const S = 128;
  const { c, g } = canvas2d(S, S);
  g.clearRect(0, 0, S, S);
  g.lineCap = 'butt';
  g.strokeStyle = '#ffffff';
  g.lineWidth = S * 0.15;
  for (let i = -2; i < 5; i++) {
    g.beginPath();
    g.moveTo(i * (S / 2) - S, -S);
    g.lineTo(i * (S / 2) + S, S);
    g.stroke();
    g.beginPath();
    g.moveTo(i * (S / 2) - S, S * 2);
    g.lineTo(i * (S / 2) + S, 0);
    g.stroke();
  }
  // Darken one diagonal family so the mesh reads three-dimensional under flat light.
  g.globalCompositeOperation = 'source-atop';
  g.strokeStyle = 'rgba(0,0,0,0.5)';
  g.lineWidth = S * 0.06;
  for (let i = -2; i < 5; i++) {
    g.beginPath();
    g.moveTo(i * (S / 2) - S, -S + S * 0.05);
    g.lineTo(i * (S / 2) + S, S + S * 0.05);
    g.stroke();
  }
  g.globalCompositeOperation = 'source-over';
  return finishTiling(c);
}

/**
 * Painted hazard striping: hard slate diagonals on white paint.
 *
 * Deliberately **not** yellow. The classic yellow-and-black band would be the obvious
 * choice, but the only warm yellow in this game's palette is `PALETTE.loaded` — the
 * colour of a chambered gun — and DESIGN.md 4 makes that signal the most important
 * readability rule in the project. Painting hundreds of linear metres of ledge in it,
 * on every drop on a five-deck map, is exactly how the one colour a player must never
 * misread becomes background noise.
 *
 * Slate on white loses nothing: the diagonal *pattern* is what says "edge", the value
 * contrast is far stronger than yellow-on-white under a high-key sun, and it costs the
 * colour budget nothing at all. The striping also runs at a 0.9 m pitch against the
 * floor's 4 m orthogonal expansion joints, so the two never read as the same mark.
 */
export function makeHazardTexture(): THREE.CanvasTexture {
  const S = 128;
  const { c, g } = canvas2d(S, S);
  g.fillStyle = hex(PALETTE.base);
  g.fillRect(0, 0, S, S);
  g.fillStyle = hex(PALETTE.ink);
  const step = S / 4;
  for (let i = -4; i < 9; i++) {
    g.beginPath();
    g.moveTo(i * step, 0);
    g.lineTo(i * step + step * 0.5, 0);
    g.lineTo(i * step + step * 0.5 - S, S);
    g.lineTo(i * step - S, S);
    g.closePath();
    g.fill();
  }
  return finishTiling(c);
}

/**
 * SINGLE-PLY ROOFING MEMBRANE — and the *pristine* pass, which is where the grime went.
 *
 * The playtest asked for "insanely pristine... a beautiful day", and this surface is the one
 * the player looks at for the entire match, so it is where that instruction lands hardest.
 * The previous version carried ninety ponding blobs at up to 8% and twenty-six traffic
 * scuffs on every two-metre tile — a working service roof, correctly observed and exactly
 * the wrong building. This is an immaculate showpiece under a perfect sky.
 *
 * So the weathering is not deleted, it is dialled to the floor: half as many ponding marks
 * at a third of the strength, no scuffing at all. What survives is *fabrication*, not dirt —
 * the welded lap at the roll seam, which is a fact about how the roof is made and is the
 * thing giving a big deck its direction and its two-metre ruler. A surface with zero texture
 * reads as an untextured greybox; a surface with dirt on it reads as neglected. The line
 * between those is narrower than it looks and it is drawn right here.
 */
export function makeMembraneTexture(): THREE.CanvasTexture {
  const S = 256;
  const { c, g } = canvas2d(S, S);
  // Warmed a touch off pure base2: sunlight on a white roof is not neutral, and a hair of
  // warmth in the largest surface in the game is most of what "sunny" means.
  g.fillStyle = hex(PALETTE.base2);
  g.fillRect(0, 0, S, S);
  g.globalAlpha = 0.4;
  g.fillStyle = hex(PALETTE.sun);
  g.fillRect(0, 0, S, S);
  g.globalAlpha = 1;

  // Ponding mottle. Deterministic, coarse, and now barely there — enough that the plane is
  // not perfectly flat, far too little to read as staining.
  let seed = 0x51ed;
  const rnd = (): number => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  g.fillStyle = hex(PALETTE.shadow);
  for (let i = 0; i < 40; i++) {
    g.globalAlpha = 0.008 + rnd() * 0.018;
    const r = S * (0.08 + rnd() * 0.22);
    g.beginPath();
    g.ellipse(rnd() * S, rnd() * S, r, r * (0.5 + rnd() * 0.6), rnd() * Math.PI, 0, Math.PI * 2);
    g.fill();
  }

  // The lap weld: a bright bead over a fine shadow line, on both tile edges so the pattern
  // is continuous however the quad is scaled.
  g.globalAlpha = 1;
  g.fillStyle = hex(PALETTE.shadow);
  g.fillRect(0, 0, S, 3);
  g.fillStyle = hex(PALETTE.base);
  g.fillRect(0, 3, S, 3);
  return finishTiling(c);
}

/**
 * Gravel ballast. The secondary roof surface, so two adjacent decks are told apart the way
 * real roofs are — by what is laid on them, not by being painted different colours.
 *
 * Lighter and noisier than the membrane, which puts it one rung *up* the value ladder: a
 * ballasted roof genuinely is brighter in sun than a grey sheet, and it means the two roof
 * types separate in greyscale, at distance, and for a colour-blind player.
 *
 * Pristine pass: the stone is washed pale rather than dirty grey. Clean ballast on a new
 * roof is bright limestone chip, and one dark tone in three was pulling the whole deck a
 * value step down — the "muted" the playtest saw. Two pale tones and a much rarer dark one
 * keeps the grain without the grime.
 */
export function makeGravelTexture(): THREE.CanvasTexture {
  const S = 256;
  const { c, g } = canvas2d(S, S);
  g.fillStyle = hex(PALETTE.base);
  g.fillRect(0, 0, S, S);
  let seed = 0x9e37;
  const rnd = (): number => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  const tones = [PALETTE.base2, PALETTE.base, PALETTE.sun, PALETTE.base2, PALETTE.shadow];
  for (let i = 0; i < 2600; i++) {
    g.fillStyle = hex(tones[(rnd() * tones.length) | 0]);
    g.globalAlpha = 0.14 + rnd() * 0.3;
    const r = 1 + rnd() * 2.6;
    g.beginPath();
    g.arc(rnd() * S, rnd() * S, r, 0, Math.PI * 2);
    g.fill();
  }
  g.globalAlpha = 1;
  return finishTiling(c);
}

/**
 * Safety banding for lifting gear: matte sun-yellow and slate diagonals.
 *
 * The counterpart to `makeHazardTexture`, and the argument for it is the same argument in
 * reverse. Ledge striping covers hundreds of linear metres, so it must not be warm — that is
 * how the gun's amber becomes background noise. A davit's jib nose covers about a square
 * metre, nineteen times in the whole arena, and it needs to be picked out of a bright sky at
 * sixty metres. A high-frequency yellow-and-slate stripe survives that distance where a flat
 * tone would wash out, it is unmistakably *equipment* rather than architecture, and being
 * matte it never blooms — so it can never be read as a chambered gun.
 */
export function makeWarnTexture(): THREE.CanvasTexture {
  const S = 128;
  const { c, g } = canvas2d(S, S);
  g.fillStyle = hex(PALETTE.accentB);
  g.fillRect(0, 0, S, S);
  g.fillStyle = hex(PALETTE.ink);
  const step = S / 3;
  for (let i = -3; i < 7; i++) {
    g.beginPath();
    g.moveTo(i * step, 0);
    g.lineTo(i * step + step * 0.5, 0);
    g.lineTo(i * step + step * 0.5 - S, S);
    g.lineTo(i * step - S, S);
    g.closePath();
    g.fill();
  }
  return finishTiling(c);
}

/**
 * AWNING CANVAS: broad terracotta and white stripes, with a hairline shadow in each seam.
 *
 * The stripe is the point. A flat coloured band on a wall is one more painted plane and it
 * washes out at range; a stripe is high-frequency, so it survives to a distance where a flat
 * tone has gone, and it is unmistakably *fabric*, which is a material this arena otherwise
 * does not contain. Under a bright sun a striped awning is about the most direct thing
 * architecture can say for "it is a beautiful day", which is exactly the note the playtest
 * asked for.
 *
 * The colour is passed in so this stays anchored to whatever the accent paints resolve to
 * upstream, rather than hard-coding a second terracotta that could drift away from the one
 * on the buildings.
 */
export function makeAwningTexture(stripe: number): THREE.CanvasTexture {
  const S = 128;
  const { c, g } = canvas2d(S, S);
  g.fillStyle = hex(PALETTE.base);
  g.fillRect(0, 0, S, S);
  g.fillStyle = hex(stripe);
  g.fillRect(0, 0, S * 0.5, S);
  // A fine shadow line down each seam: real canvas is sewn, and the seam is what stops two
  // flat bands reading as a printed graphic.
  g.globalAlpha = 0.22;
  g.fillStyle = hex(PALETTE.shadow);
  g.fillRect(S * 0.5 - 1, 0, 2, S);
  g.fillRect(0, 0, 1, S);
  g.fillRect(S - 1, 0, 1, S);
  g.globalAlpha = 1;
  return finishTiling(c);
}

/**
 * Glazing bands for the skyline.
 *
 * Daytime: a distant tower's windows are mirrors, not lamps. Each pane gets a slightly
 * different slice of sky, which is what makes a glass facade shimmer — and it keeps the
 * whole horizon inside the sky/white family instead of dotting it with coloured lights.
 */
export function makeGlazingTexture(rng: () => number): THREE.CanvasTexture {
  const S = 256;
  const { c, g } = canvas2d(S, S);
  g.clearRect(0, 0, S, S);
  const cols = 12;
  const rows = 8;
  const cw = S / cols;
  const rh = S / rows;
  // Sky reflected off glass at grazing angles: the horizon tone through to near-white.
  const tints = [PALETTE.sky, PALETTE.skyHigh, PALETTE.base, PALETTE.base2, PALETTE.sun];
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      g.fillStyle = hex(tints[(rng() * tints.length) | 0]);
      g.globalAlpha = 0.28 + rng() * 0.42;
      g.fillRect(i * cw + cw * 0.1, j * rh + rh * 0.18, cw * 0.8, rh * 0.56);
    }
  }
  // Spandrel line under each band of glass — the horizontal that says "storeys".
  g.globalAlpha = 0.5;
  g.fillStyle = hex(PALETTE.shadow);
  for (let j = 0; j < rows; j++) g.fillRect(0, j * rh + rh * 0.78, S, Math.max(1, rh * 0.1));
  g.globalAlpha = 1;
  return finishTiling(c, 4);
}

/** The grid far below the kill plane: falling has to look like falling. */
export function makeVoidTexture(): THREE.CanvasTexture {
  const S = 256;
  const { c, g } = canvas2d(S, S);
  g.clearRect(0, 0, S, S);
  g.strokeStyle = hex(PALETTE.shadow);
  g.globalAlpha = 0.9;
  g.lineWidth = 4;
  g.strokeRect(0, 0, S, S);
  g.globalAlpha = 0.25;
  g.lineWidth = 1;
  for (let i = 1; i < 4; i++) {
    g.beginPath();
    g.moveTo((S * i) / 4, 0);
    g.lineTo((S * i) / 4, S);
    g.stroke();
    g.beginPath();
    g.moveTo(0, (S * i) / 4);
    g.lineTo(S, (S * i) / 4);
    g.stroke();
  }
  g.globalAlpha = 1;
  return finishTiling(c, 4);
}
